import Backbone from "backbone";
import FileUploadHelper from "models/helper/FileUploadHelper";
import SoundManagerMedia from "models/helper/SoundManagerMedia";
import SoundNativeAudio from "models/helper/SoundNativeAudio";
import TrebbleRecorder from "models/helper/TrebbleRecorder";
import FeatureRolloutHelper from "models/helper/FeatureRolloutHelper";
import BrowserRecordingDraftStore from "models/helper/BrowserRecordingDraftStore";
import PersistentModels from "services/PersistentModels";
import LocalStorageHelper from "models/helper/LocalStorageHelper";
import Utils from "models/helper/Utils";
import ti18n from "i18n!nls/helperi18n";
import RSVP from "rsvp";
import unmute from "libs/unmute/unmute";

const USE_CORDOVA_MEDIA_FOR_BACKGROUND = FeatureRolloutHelper.getInstance().getFeatureVariable(FeatureRolloutHelper.FEATURES.ALLOW_USE_MUSIC_WHILE_RECORDING_CAPSULE_ON_MOBILE, FeatureRolloutHelper.FEATURES.ALLOW_USE_MUSIC_WHILE_RECORDING_CAPSULE_ON_MOBILE.variables.use_cordova_media, false);
const USE_NATIVE_AUDIO_FOR_BACKGROUND = FeatureRolloutHelper.getInstance().getFeatureVariable(FeatureRolloutHelper.FEATURES.ALLOW_USE_MUSIC_WHILE_RECORDING_CAPSULE_ON_MOBILE, FeatureRolloutHelper.FEATURES.ALLOW_USE_MUSIC_WHILE_RECORDING_CAPSULE_ON_MOBILE.variables.use_native_audio, true);
let INTRO_IN_SEC = FeatureRolloutHelper.getInstance().getFeatureVariable(FeatureRolloutHelper.FEATURES.CAPSULE_RECORDING_UI_DEFAULT_CONFIG, FeatureRolloutHelper.FEATURES.CAPSULE_RECORDING_UI_DEFAULT_CONFIG.variables.background_music_intro_length_in_sec, null);
let FADE_OUT_END_IN_SEC = FeatureRolloutHelper.getInstance().getFeatureVariable(FeatureRolloutHelper.FEATURES.CAPSULE_RECORDING_UI_DEFAULT_CONFIG, FeatureRolloutHelper.FEATURES.CAPSULE_RECORDING_UI_DEFAULT_CONFIG.variables.background_music_fade_out_end_in_sec, null);
let INTRO_FADE_IN_SEC = FeatureRolloutHelper.getInstance().getFeatureVariable(FeatureRolloutHelper.FEATURES.CAPSULE_RECORDING_UI_DEFAULT_CONFIG, FeatureRolloutHelper.FEATURES.CAPSULE_RECORDING_UI_DEFAULT_CONFIG.variables.background_fade_in_outro_in_sec, null);
let OUTRO_FADE_IN_SEC = FeatureRolloutHelper.getInstance().getFeatureVariable(FeatureRolloutHelper.FEATURES.CAPSULE_RECORDING_UI_DEFAULT_CONFIG, FeatureRolloutHelper.FEATURES.CAPSULE_RECORDING_UI_DEFAULT_CONFIG.variables.background_fade_out_intro_in_sec, null);
const BACKGROUND_VOLUME_LEVEL = FeatureRolloutHelper.getInstance().getFeatureVariable(FeatureRolloutHelper.FEATURES.CAPSULE_RECORDING_UI_DEFAULT_CONFIG, FeatureRolloutHelper.FEATURES.CAPSULE_RECORDING_UI_DEFAULT_CONFIG.variables.background_volume_level, 50);
const FADE_INCREMENT = FeatureRolloutHelper.getInstance().getFeatureVariable(FeatureRolloutHelper.FEATURES.CAPSULE_RECORDING_UI_DEFAULT_CONFIG, FeatureRolloutHelper.FEATURES.CAPSULE_RECORDING_UI_DEFAULT_CONFIG.variables.fade_increment, null);
const BACKGROUND_BUFFER_AT_THE_END_IN_SEC = FeatureRolloutHelper.getInstance().getFeatureVariable(FeatureRolloutHelper.FEATURES.CAPSULE_RECORDING_UI_DEFAULT_CONFIG, FeatureRolloutHelper.FEATURES.CAPSULE_RECORDING_UI_DEFAULT_CONFIG.variables.background_music_end_buffer_in_sec, 2);
const ALWAYS_USE_MEDIA_RECORDERJS = FeatureRolloutHelper.getInstance().getFeatureVariable(FeatureRolloutHelper.FEATURES.CAPSULE_RECORDING_UI_DEFAULT_CONFIG, FeatureRolloutHelper.FEATURES.CAPSULE_RECORDING_UI_DEFAULT_CONFIG.variables.always_use_media_recordjs, false);
const VALIDATE_MIC_SIGNAL_BEFORE_RECORDING = FeatureRolloutHelper.getInstance().getFeatureVariable(FeatureRolloutHelper.FEATURES.CAPSULE_RECORDING_UI_DEFAULT_CONFIG, FeatureRolloutHelper.FEATURES.CAPSULE_RECORDING_UI_DEFAULT_CONFIG.variables.validate_mic_signal_before_recording, true);
const DEFAULT_RECORDING_MEDIA_TYPE = "audio";
const DEFAULT_VIDEO_CONSTRAINTS = {
    facingMode: "user"
};
const USER_MEDIA_REQUEST_TIMEOUT_IN_MILLI = 8000;
const USER_MEDIA_REQUEST_TIMEOUT_WHEN_PERMISSION_STATE_IS_UNKNOWN_IN_MILLI = 30000;
const VIDEO_STREAM_VALIDATION_TIMEOUT_IN_MILLI = 4000;
// Must exceed the worst-case inner budget in Utils.getMediaMetadata:
// MEDIA_METADATA_LOAD_TIMEOUT (8s) + VIDEO_DURATION_RESOLUTION_TIMEOUT (2.5s) + VIDEO_THUMBNAIL_CAPTURE_TIMEOUT (2s) = 12.5s.
// A shorter value aborts the extraction prematurely and produces null durations for mp4 clips that report Infinity duration and require the seek-probe path.
const RECORDED_MEDIA_METADATA_TIMEOUT_IN_MILLI = 15000;
const RECORDED_MEDIA_LOCAL_URL_TIMEOUT_IN_MILLI = 4000;
const MEDIA_CAPTURE_ERROR_REASON = {
    PERMISSION_DENIED: "permission_denied",
    NOT_FOUND: "not_found",
    DEVICE_BUSY: "device_busy",
    TIMEOUT: "timeout",
    ABORTED: "aborted",
    UNSUPPORTED: "unsupported",
    NO_AUDIO_SIGNAL: "no_audio_signal",
    UNKNOWN: "unknown"
};
const NO_AUDIO_SIGNAL_ERROR_NAME = "NoAudioSignalError";
const AUDIO_SIGNAL_PROBE_FAILURE_REASON = {
    TRACK_ENDED: "track_ended",
    TRACK_MUTED: "track_muted",
    SILENT: "silent"
};
const AUDIO_STREAM_VALIDATION_TIMEOUT_IN_MILLI = 2500;
const AUDIO_SIGNAL_PROBE_POLL_INTERVAL_IN_MILLI = 50;
const AUDIO_SIGNAL_PROBE_CONTEXT_STARTUP_GRACE_IN_MILLI = 400;
const AUDIO_SIGNAL_PROBE_FFT_SIZE = 2048;
const USER_MEDIA_PERMISSION_ERROR_NAMES = ["NotAllowedError", "PermissionDeniedError", "SecurityError"];
const USER_MEDIA_NOT_FOUND_ERROR_NAMES = ["NotFoundError", "DevicesNotFoundError"];
const USER_MEDIA_BUSY_ERROR_NAMES = ["NotReadableError", "TrackStartError"];
const USER_MEDIA_ABORT_ERROR_NAMES = ["AbortError"];
const USER_MEDIA_CONSTRAINT_ERROR_NAMES = ["OverconstrainedError", "ConstraintNotSatisfiedError"];
const USER_MEDIA_UNSUPPORTED_ERROR_NAMES = ["NotSupportedError"];
const MIN_RELIABLE_AUDIO_RECORDING_SIZE_IN_BYTES = 128;
const MIN_RELIABLE_AUDIO_RECORDING_SIZE_OVER_ONE_SECOND_IN_BYTES = 512;
const MIN_RELIABLE_VIDEO_RECORDING_SIZE_IN_BYTES = 512;
const MIN_RELIABLE_VIDEO_RECORDING_SIZE_OVER_ONE_SECOND_IN_BYTES = 2048;
const WEBM_EBML_HEADER_BYTES = [0x1A, 0x45, 0xDF, 0xA3];
const EBML_MAX_ELEMENT_ID_LENGTH_IN_BYTES = 4;
const EBML_MAX_SIZE_LENGTH_IN_BYTES = 8;
const WEBM_DOCTYPE_ELEMENT_ID = 0x4282;
const SUPPORTED_WEBM_DOCTYPES = ["webm", "matroska"];
const WEBM_HEADER_SCAN_CHUNK_SIZE_IN_BYTES = 1024 * 1024;
const WEBM_HEADER_SCAN_OVERLAP_IN_BYTES = 16 * 1024;
const BEST_AVAILABLE_VIDEO_MAX_DIMENSION = 7680;
const AUTO_VIDEO_PREVIEW_WIDTH = 1280;
const AUTO_VIDEO_PREVIEW_HEIGHT = 720;
const PREFERRED_WIDESCREEN_VIDEO_ASPECT_RATIO = 16 / 9;
const PREFERRED_WIDESCREEN_VIDEO_ASPECT_RATIO_TOLERANCE = 0.12;
const NATIVE_VIDEO_RECORDING_UNSUPPORTED_ERROR = "Native video recording is not supported yet.";

const shouldUseMediaRecorderForAudioRecording = function() {
    return !ALWAYS_USE_MEDIA_RECORDERJS && typeof window !== "undefined" && window && "MediaRecorder" in window && !!TrebbleRecorder.getSupportedRecorderMimeInfo("audio");
};

const buildVideoConstraintPreset = function(resolution) {
    if (resolution === "auto") {
        return {
            width: { ideal: BEST_AVAILABLE_VIDEO_MAX_DIMENSION },
            height: { ideal: BEST_AVAILABLE_VIDEO_MAX_DIMENSION }
        };
    }

    if (resolution === "4k") {
        return {
            width: { ideal: 3840 },
            height: { ideal: 2160 },
            aspectRatio: { ideal: PREFERRED_WIDESCREEN_VIDEO_ASPECT_RATIO }
        };
    }

    if (resolution === "1080p") {
        return {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            aspectRatio: { ideal: PREFERRED_WIDESCREEN_VIDEO_ASPECT_RATIO }
        };
    }

    if (resolution === "720p") {
        return {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            aspectRatio: { ideal: PREFERRED_WIDESCREEN_VIDEO_ASPECT_RATIO }
        };
    }

    return null;
};

const buildVideoPreviewConstraintPreset = function(resolution) {
    if (resolution === "auto") {
        return {
            width: { ideal: AUTO_VIDEO_PREVIEW_WIDTH },
            height: { ideal: AUTO_VIDEO_PREVIEW_HEIGHT },
            aspectRatio: { ideal: PREFERRED_WIDESCREEN_VIDEO_ASPECT_RATIO }
        };
    }

    return buildVideoConstraintPreset(resolution);
};

const buildVideoConstraintsForResolution = function(resolution, options) {
    const constraintOptions = options || {};
    const includePreferredDeviceId = constraintOptions.includePreferredDeviceId !== false;
    const preferredVideoDeviceId = includePreferredDeviceId ? LocalStorageHelper.getInstance().getPreferredDeviceVideoId() : null;
    const preferredVideoConstraintPreset = constraintOptions.previewOnly ? buildVideoPreviewConstraintPreset(resolution) : buildVideoConstraintPreset(resolution);
    const videoConstraints = Object.assign({}, DEFAULT_VIDEO_CONSTRAINTS, preferredVideoConstraintPreset || {});

    if (preferredVideoDeviceId) {
        videoConstraints.deviceId = { exact: preferredVideoDeviceId };
        delete videoConstraints.facingMode;
    }

    return videoConstraints;
};

const buildVideoResolutionCandidateList = function(resolution) {
    if (resolution === "auto") {
        return ["auto"];
    }

    if (resolution === "4k" || resolution === "1080p" || resolution === "720p") {
        return [resolution];
    }

    return ["auto"];
};

const buildAudioConstraints = function(options) {
    const constraintOptions = options || {};
    const includePreferredDeviceId = constraintOptions.includePreferredDeviceId !== false;
    const preferredAudioDeviceId = includePreferredDeviceId ? LocalStorageHelper.getInstance().getPreferredDeviceAudioId() : null;
    const audioConstraints = preferredAudioDeviceId ? { deviceId: preferredAudioDeviceId } : {};
    audioConstraints.echoCancellation = LocalStorageHelper.getInstance().getRecordingEchoCancellationEnabled();
    audioConstraints.noiseSuppression = LocalStorageHelper.getInstance().getRecordingNoiseSuppressionEnabled();
    audioConstraints.autoGainControl = LocalStorageHelper.getInstance().getRecordingAutoGainControlEnabled();
    audioConstraints.channelCount = 1;
    return audioConstraints;
};

const buildVideoConstraints = function(options) {
    const constraintOptions = options || {};
    const preferredVideoResolution = LocalStorageHelper.getInstance().getRecordingVideoResolution();
    return buildVideoConstraintsForResolution(preferredVideoResolution, constraintOptions);
};

const stopStreamTracks = function(stream) {
    if (stream && stream.getTracks) {
        stream.getTracks().forEach(function(track) {
            try {
                track.stop();
            } catch (_) {
                // Ignore cleanup failures when releasing probe streams.
            }
        });
    }
};

const buildTimeoutError = function(message) {
    const error = new Error(message || "Timed out while starting media input.");
    error.name = "TimeoutError";
    return error;
};

const getErrorMessage = function(error) {
    if (!error) {
        return "";
    }

    if (typeof error === "string") {
        return error;
    }

    return error.message || "";
};

const isRecoverableUserMediaError = function(error) {
    const recoverableErrorNames = ["AbortError", "NotFoundError", "NotReadableError", "OverconstrainedError", "TimeoutError"];
    return !!(error && recoverableErrorNames.indexOf(error.name) !== -1);
};

const dedupeConstraintCandidates = function(constraintCandidates) {
    const uniqueConstraintCandidates = [];
    const keysSeen = {};

    (constraintCandidates || []).forEach(function(candidate) {
        if (!candidate) {
            return;
        }

        const candidateKey = JSON.stringify(candidate);
        if (!keysSeen[candidateKey]) {
            keysSeen[candidateKey] = true;
            uniqueConstraintCandidates.push(candidate);
        }
    });

    return uniqueConstraintCandidates;
};

const dedupeMediaRequestCandidates = function(requestCandidates) {
    const uniqueRequestCandidates = [];
    const keysSeen = {};

    (requestCandidates || []).forEach(function(candidate) {
        if (!candidate) {
            return;
        }

        const normalizedCandidate = candidate.constraints ? candidate : {
            constraints: candidate
        };
        const candidateKey = JSON.stringify({
            constraints: normalizedCandidate.constraints,
            validationOptions: normalizedCandidate.validationOptions || null
        });

        if (!keysSeen[candidateKey]) {
            keysSeen[candidateKey] = true;
            uniqueRequestCandidates.push(normalizedCandidate);
        }
    });

    return uniqueRequestCandidates;
};

const buildVideoValidationOptionsForResolution = function(resolution) {
    // Let the browser satisfy fixed quality requests with the selected camera's
    // native aspect ratio. The preview/layout code adapts to the resolved
    // stream dimensions, so rejecting non-16:9 cameras here only forces
    // unnecessary fallback onto a different device.
    return null;
};

const hasVisibleDeviceLabel = function(device) {
    return !!(device && device.label && device.label.trim());
};

const isTrackLive = function(track) {
    return !!(track && track.readyState === "live");
};

const isStreamActive = function(stream) {
    if (!stream || !stream.getTracks) {
        return false;
    }

    return stream.getTracks().some(isTrackLive);
};

const buildNoAudioSignalError = function(probeFailureReason, deviceLabel) {
    const error = new Error("No audio signal was detected from the selected microphone.");
    error.name = NO_AUDIO_SIGNAL_ERROR_NAME;
    error.noAudioSignalReason = probeFailureReason;
    error.deviceLabel = deviceLabel || null;
    return error;
};

const resolveAudioSignalProbeOutcome = function(probeState) {
    if (probeState.sawAudioSignal) {
        return { passed: true, failureReason: null };
    }

    if (probeState.trackEnded) {
        return { passed: false, failureReason: AUDIO_SIGNAL_PROBE_FAILURE_REASON.TRACK_ENDED };
    }

    if (probeState.trackMuted) {
        return { passed: false, failureReason: AUDIO_SIGNAL_PROBE_FAILURE_REASON.TRACK_MUTED };
    }

    if (probeState.couldAnalyseSignal) {
        return { passed: false, failureReason: AUDIO_SIGNAL_PROBE_FAILURE_REASON.SILENT };
    }

    // The probe could not assess the stream (no WebAudio or a context that never started):
    // fail open so a healthy setup is never blocked by the probe's own instrumentation.
    return { passed: true, failureReason: null };
};

const probeAudioStreamForSignal = function(stream, options) {
    const probeOptions = options || {};
    const audioTrack = stream && stream.getAudioTracks ? stream.getAudioTracks()[0] : null;
    if (!audioTrack) {
        // Audio track presence is enforced by _validateBrowserStream before the probe runs.
        return RSVP.Promise.resolve(stream);
    }

    const timeoutInMilli = probeOptions.timeoutInMilli || AUDIO_STREAM_VALIDATION_TIMEOUT_IN_MILLI;
    const pollIntervalInMilli = probeOptions.pollIntervalInMilli || AUDIO_SIGNAL_PROBE_POLL_INTERVAL_IN_MILLI;
    const contextStartupGraceInMilli = probeOptions.contextStartupGraceInMilli || AUDIO_SIGNAL_PROBE_CONTEXT_STARTUP_GRACE_IN_MILLI;

    return new RSVP.Promise(function(resolve, reject) {
        const probeStartTime = Date.now();
        let settled = false;
        let pollIntervalId = null;
        let deadlineTimeoutId = null;
        let audioContext = null;
        let sourceNode = null;
        let analyserNode = null;
        let sampleBuffer = null;
        let usesFloatSamples = false;

        try {
            const AudioContextConstructor = probeOptions.audioContextConstructor || (typeof window !== "undefined" ? (window.AudioContext || window.webkitAudioContext) : null);
            if (AudioContextConstructor) {
                audioContext = new AudioContextConstructor();
                sourceNode = audioContext.createMediaStreamSource(stream);
                analyserNode = audioContext.createAnalyser();
                analyserNode.fftSize = AUDIO_SIGNAL_PROBE_FFT_SIZE;
                sourceNode.connect(analyserNode);
                usesFloatSamples = typeof analyserNode.getFloatTimeDomainData === "function";
                sampleBuffer = usesFloatSamples ? new Float32Array(analyserNode.fftSize) : new Uint8Array(analyserNode.fftSize);
                if (audioContext.state === "suspended" && audioContext.resume) {
                    audioContext.resume().catch(function() {
                        // The deadline outcome degrades gracefully when the context never runs.
                    });
                }
            }
        } catch (analyserSetupError) {
            console.warn("Unable to set up the microphone signal analyser. Only the track mute state will be probed.", analyserSetupError);
            analyserNode = null;
            sampleBuffer = null;
        }

        const cleanupProbe = function() {
            if (pollIntervalId !== null) {
                clearInterval(pollIntervalId);
                pollIntervalId = null;
            }
            if (deadlineTimeoutId !== null) {
                clearTimeout(deadlineTimeoutId);
                deadlineTimeoutId = null;
            }
            try {
                if (sourceNode && sourceNode.disconnect) {
                    sourceNode.disconnect();
                }
            } catch (_) {
                // Ignore cleanup failures for the probe source node.
            }
            try {
                if (analyserNode && analyserNode.disconnect) {
                    analyserNode.disconnect();
                }
            } catch (_) {
                // Ignore cleanup failures for the probe analyser node.
            }
            try {
                if (audioContext && audioContext.close) {
                    audioContext.close();
                }
            } catch (_) {
                // Ignore cleanup failures for the ephemeral probe audio context.
            }
        };

        const settleWithPass = function() {
            if (settled) {
                return;
            }
            settled = true;
            cleanupProbe();
            resolve(stream);
        };

        const settleWithFailure = function(probeFailureReason) {
            if (settled) {
                return;
            }
            settled = true;
            cleanupProbe();
            reject(buildNoAudioSignalError(probeFailureReason, audioTrack.label));
        };

        const isTrackEnded = function() {
            return audioTrack.readyState === "ended";
        };

        const isTrackMuted = function() {
            return audioTrack.muted === true;
        };

        const canAnalyseSignal = function() {
            return !!(analyserNode && audioContext && audioContext.state === "running");
        };

        const hasDetectableSignal = function() {
            if (!analyserNode || !sampleBuffer) {
                return false;
            }
            if (usesFloatSamples) {
                analyserNode.getFloatTimeDomainData(sampleBuffer);
                for (let sampleIndex = 0; sampleIndex < sampleBuffer.length; sampleIndex++) {
                    if (sampleBuffer[sampleIndex] !== 0) {
                        return true;
                    }
                }
                return false;
            }
            analyserNode.getByteTimeDomainData(sampleBuffer);
            for (let sampleIndex = 0; sampleIndex < sampleBuffer.length; sampleIndex++) {
                if (sampleBuffer[sampleIndex] !== 128) {
                    return true;
                }
            }
            return false;
        };

        const onProbeTick = function(reachedDeadline) {
            if (settled) {
                return;
            }

            if (isTrackEnded()) {
                settleWithFailure(AUDIO_SIGNAL_PROBE_FAILURE_REASON.TRACK_ENDED);
                return;
            }

            let sawAudioSignal = false;
            try {
                sawAudioSignal = hasDetectableSignal();
            } catch (samplingError) {
                console.warn("Unable to sample the microphone signal. Only the track mute state will be probed.", samplingError);
                analyserNode = null;
                sampleBuffer = null;
            }

            if (sawAudioSignal) {
                settleWithPass();
                return;
            }

            if (!reachedDeadline) {
                // Fail open early when the signal cannot be analysed (no WebAudio, or a context that
                // never starts) while the browser reports media flowing, so healthy setups are never
                // delayed by the probe's own instrumentation.
                const startupGraceElapsed = Date.now() - probeStartTime >= contextStartupGraceInMilli;
                if (!isTrackMuted() && (!analyserNode || (startupGraceElapsed && !canAnalyseSignal()))) {
                    settleWithPass();
                }
                return;
            }

            const probeOutcome = resolveAudioSignalProbeOutcome({
                sawAudioSignal: false,
                trackEnded: isTrackEnded(),
                trackMuted: isTrackMuted(),
                couldAnalyseSignal: canAnalyseSignal()
            });

            if (probeOutcome.passed) {
                if (!canAnalyseSignal()) {
                    console.warn("The microphone signal could not be analysed before recording. Continuing without the signal check.");
                }
                settleWithPass();
            } else {
                settleWithFailure(probeOutcome.failureReason);
            }
        };

        pollIntervalId = setInterval(function() {
            onProbeTick(false);
        }, pollIntervalInMilli);
        deadlineTimeoutId = setTimeout(function() {
            onProbeTick(true);
        }, timeoutInMilli);
        onProbeTick(false);
    });
};

const AudioRecorderHelper = Backbone.Model.extend({


    initialize: function() {
        this.applyGetUserMediaPatchForOldBrowsers();
        this._recordMediaObjects = {};
        this._recordMediaFilenames = {};
        this._recordFilenameToBlob = {};
        this._recordFilenameToMediaInfo = {};
        this._recordFilenameToDurableDraftManifest = {};
        this._isPlayingRecording = false;
        this._recordingFilenameInPlayQueue = [];
        this._backgroundTrackUrl = null;
        this._backgroundTrackMedia = null;
        this._playRecordingRequestId = null;
        this._backgroundTrackLoadingPromise = null;
        this._currentMediaPlaying = null;
        this._currentMediaPlayingStatus = null;
        this._onMediaSuccessfullyStoppedCBs = [];
        this._onMediaFailedBeingStoppedCBs = [];
        this._currentlyLoadedMediaNeedToBeStopped = false;
        this._userHasPermissionToRecordInThisSession = {
            audio: false,
            video: false
        };
        this._recordingPromise = null;
        this._isRecording = false;
        this._recordingMediaType = DEFAULT_RECORDING_MEDIA_TYPE;
        this._videoPreviewStream = null;
        this._videoPreviewPromise = null;
        this._videoPreviewStopPromise = RSVP.Promise.resolve();
        this._videoPreviewSourceType = null;
        this._videoPreviewPendingSourceType = null;
        this._videoPreviewRequestId = 0;
        this.deleteAllBackgroundMusicDownloadedLocally();


    },

    getRecordFilenameToBlob: function() {
        return this._recordFilenameToBlob;
    },

    getRecordingPromise: function() {
        return this._recordingPromise || RSVP.Promise.resolve();
    },

    getRecordFilenameToMediaInfo: function() {
        return this._recordFilenameToMediaInfo;
    },

    getRecordingMediaType: function() {
        return this._recordingMediaType || DEFAULT_RECORDING_MEDIA_TYPE;
    },

    setRecordingMediaType: function(recordingMediaType) {
        this._recordingMediaType = recordingMediaType === "video" ? "video" : "audio";
    },

    getRecordingFileInfo: function(recordingMediaType) {
        const mediaType = recordingMediaType === "video" ? "video" : "audio";
        const shouldUseMediaRecorderForAudio = shouldUseMediaRecorderForAudioRecording();
        const supportedMimeType = mediaType === "video" || shouldUseMediaRecorderForAudio ? TrebbleRecorder.getSupportedRecorderMimeInfo(mediaType) : "audio/wav";
        let fileExtension = Utils.getInstance().getFileExtenstionForMimeType(supportedMimeType);

        if (!fileExtension) {
            fileExtension = mediaType === "video" ? "webm" : Utils.getInstance().getAudioRecordedFileExtention();
        }

        return {
            mediaType,
            mimeType: supportedMimeType,
            fileExtension
        };
    },

    isRecordingMediaTypeSupported: function(recordingMediaType) {
        const mediaType = recordingMediaType === "video" ? "video" : "audio";
        if (mediaType !== "video") {
            return true;
        }

        return !!(window.device && window.device.platform == "browser" && !(window.trebble.useNativeRecording && "Media" in window));
    },

    _getMediaCaptureInputLabel: function(recordingMediaType, options) {
        const labelOptions = options || {};
        const mediaType = recordingMediaType === "video" ? "video" : "audio";
        const context = labelOptions.context === "preview" ? "preview" : "recording";
        let i18nKey = "MICROPHONE_INPUT_LABEL";

        if (context === "preview") {
            i18nKey = "CAMERA_INPUT_LABEL";
        } else if (mediaType === "video") {
            i18nKey = "CAMERA_AND_MICROPHONE_INPUT_LABEL";
        }

        return window.getI18n(ti18n, i18nKey);
    },

    _getMediaCaptureErrorReason: function(error) {
        const errorName = error && error.name ? error.name : null;
        const errorMessage = getErrorMessage(error);
        const normalizedErrorMessage = errorMessage ? errorMessage.toLowerCase() : "";
        const recordingNotSupportedMessage = window.getI18n(ti18n, "RECORDING_IS_NOT_SUPPORTED");

        if (errorName === NO_AUDIO_SIGNAL_ERROR_NAME) {
            return MEDIA_CAPTURE_ERROR_REASON.NO_AUDIO_SIGNAL;
        }

        if (errorName === "TimeoutError" || normalizedErrorMessage.indexOf("timed out") !== -1) {
            return MEDIA_CAPTURE_ERROR_REASON.TIMEOUT;
        }

        if (errorName && USER_MEDIA_PERMISSION_ERROR_NAMES.indexOf(errorName) !== -1) {
            return MEDIA_CAPTURE_ERROR_REASON.PERMISSION_DENIED;
        }

        if (normalizedErrorMessage.indexOf("permission") !== -1 && (normalizedErrorMessage.indexOf("denied") !== -1 || normalizedErrorMessage.indexOf("not allowed") !== -1)) {
            return MEDIA_CAPTURE_ERROR_REASON.PERMISSION_DENIED;
        }

        if (errorName && USER_MEDIA_NOT_FOUND_ERROR_NAMES.indexOf(errorName) !== -1) {
            return MEDIA_CAPTURE_ERROR_REASON.NOT_FOUND;
        }

        if (errorName && USER_MEDIA_CONSTRAINT_ERROR_NAMES.indexOf(errorName) !== -1) {
            return MEDIA_CAPTURE_ERROR_REASON.NOT_FOUND;
        }

        if (normalizedErrorMessage.indexOf("not found") !== -1 || normalizedErrorMessage.indexOf("requested device not found") !== -1) {
            return MEDIA_CAPTURE_ERROR_REASON.NOT_FOUND;
        }

        if (errorName && USER_MEDIA_BUSY_ERROR_NAMES.indexOf(errorName) !== -1) {
            return MEDIA_CAPTURE_ERROR_REASON.DEVICE_BUSY;
        }

        if (normalizedErrorMessage.indexOf("not readable") !== -1 || normalizedErrorMessage.indexOf("track start") !== -1 || normalizedErrorMessage.indexOf("device in use") !== -1 || normalizedErrorMessage.indexOf("could not start") !== -1) {
            return MEDIA_CAPTURE_ERROR_REASON.DEVICE_BUSY;
        }

        if (errorName && USER_MEDIA_ABORT_ERROR_NAMES.indexOf(errorName) !== -1) {
            return MEDIA_CAPTURE_ERROR_REASON.ABORTED;
        }

        if (normalizedErrorMessage.indexOf("aborted") !== -1 || normalizedErrorMessage.indexOf("interrupted") !== -1) {
            return MEDIA_CAPTURE_ERROR_REASON.ABORTED;
        }

        if (errorName && USER_MEDIA_UNSUPPORTED_ERROR_NAMES.indexOf(errorName) !== -1) {
            return MEDIA_CAPTURE_ERROR_REASON.UNSUPPORTED;
        }

        if (errorMessage === recordingNotSupportedMessage || normalizedErrorMessage.indexOf("not supported") !== -1) {
            return MEDIA_CAPTURE_ERROR_REASON.UNSUPPORTED;
        }

        return MEDIA_CAPTURE_ERROR_REASON.UNKNOWN;
    },

    getMediaCaptureErrorInfo: function(error, recordingMediaType, options) {
        const errorOptions = options || {};
        const mediaType = recordingMediaType === "video" ? "video" : this.getRecordingMediaType();
        const context = errorOptions.context === "preview" ? "preview" : "recording";
        const reason = this._getMediaCaptureErrorReason(error);
        const inputLabel = this._getMediaCaptureInputLabel(mediaType, { context: context });
        const errorMessage = getErrorMessage(error);
        const params = {
            inputLabel: inputLabel,
            alternativeMessage: context === "preview" ? "" : this.getRecordingAlternativeMessageInstruction(mediaType)
        };
        let titleKey = "RECORDING_COULDNT_START_TITLE";
        let messageKey = "RECORDING_DEVICE_UNAVAILABLE_MESSAGE";

        if (context === "preview") {
            titleKey = "CAMERA_PREVIEW_UNAVAILABLE_TITLE";
            messageKey = "CAMERA_PREVIEW_UNAVAILABLE";

            if (reason === MEDIA_CAPTURE_ERROR_REASON.PERMISSION_DENIED) {
                messageKey = "CAMERA_PREVIEW_PERMISSION_REQUIRED";
            } else if (reason === MEDIA_CAPTURE_ERROR_REASON.NOT_FOUND) {
                messageKey = "CAMERA_PREVIEW_DEVICE_NOT_FOUND";
            } else if (reason === MEDIA_CAPTURE_ERROR_REASON.DEVICE_BUSY) {
                messageKey = "CAMERA_PREVIEW_DEVICE_BUSY";
            } else if (reason === MEDIA_CAPTURE_ERROR_REASON.TIMEOUT) {
                messageKey = "CAMERA_PREVIEW_DEVICE_TIMEOUT";
            } else if (reason === MEDIA_CAPTURE_ERROR_REASON.ABORTED) {
                messageKey = "CAMERA_PREVIEW_DEVICE_ABORTED";
            } else if (reason === MEDIA_CAPTURE_ERROR_REASON.UNSUPPORTED) {
                titleKey = "CAMERA_PREVIEW_UNAVAILABLE_TITLE";
                messageKey = "CAMERA_PREVIEW_UNAVAILABLE";
            }
        } else {
            if (reason === MEDIA_CAPTURE_ERROR_REASON.PERMISSION_DENIED) {
                titleKey = "RECORDING_PERMISSION_REQUIRED_TITLE";
                messageKey = "RECORDING_PERMISSION_REQUIRED_MESSAGE";
            } else if (reason === MEDIA_CAPTURE_ERROR_REASON.NOT_FOUND) {
                titleKey = "RECORDING_DEVICE_NOT_FOUND_TITLE";
                messageKey = "RECORDING_DEVICE_NOT_FOUND_MESSAGE";
            } else if (reason === MEDIA_CAPTURE_ERROR_REASON.DEVICE_BUSY) {
                titleKey = "RECORDING_DEVICE_BUSY_TITLE";
                messageKey = "RECORDING_DEVICE_BUSY_MESSAGE";
            } else if (reason === MEDIA_CAPTURE_ERROR_REASON.TIMEOUT) {
                titleKey = "RECORDING_DEVICE_TIMEOUT_TITLE";
                messageKey = "RECORDING_DEVICE_TIMEOUT_MESSAGE";
            } else if (reason === MEDIA_CAPTURE_ERROR_REASON.ABORTED) {
                titleKey = "RECORDING_COULDNT_START_TITLE";
                messageKey = "RECORDING_DEVICE_ABORTED_MESSAGE";
            } else if (reason === MEDIA_CAPTURE_ERROR_REASON.UNSUPPORTED) {
                titleKey = "RECORDING_COULDNT_START_TITLE";
                messageKey = "RECORDING_IS_NOT_SUPPORTED";
            } else if (reason === MEDIA_CAPTURE_ERROR_REASON.NO_AUDIO_SIGNAL) {
                titleKey = "MICROPHONE_NO_SIGNAL_TITLE";
                messageKey = error && error.deviceLabel ? "MICROPHONE_NO_SIGNAL_MESSAGE" : "MICROPHONE_NO_SIGNAL_GENERIC_MESSAGE";
                params.deviceLabel = error && error.deviceLabel ? error.deviceLabel : "";
            }
        }

        return {
            title: window.getI18n(ti18n, titleKey, params) || window.getI18n(ti18n, "RECORDING_COULDNT_START_TITLE"),
            message: window.getI18n(ti18n, messageKey, params) || errorMessage || window.getI18n(ti18n, "RECORDING_DEVICE_UNAVAILABLE_MESSAGE", params),
            reason: reason,
            isPermissionError: reason === MEDIA_CAPTURE_ERROR_REASON.PERMISSION_DENIED,
            isUnsupportedError: reason === MEDIA_CAPTURE_ERROR_REASON.UNSUPPORTED,
            isNoAudioSignalError: reason === MEDIA_CAPTURE_ERROR_REASON.NO_AUDIO_SIGNAL,
            error: error
        };
    },

    isAudioContextSuspended: function() {
        return (window._trebbleAudioContext && window._trebbleAudioContext.state === 'suspended');
    },

    resumeCurrentAudioContext: function() {
        return (window._trebbleAudioContext && window._trebbleAudioContext.state === 'suspended') ? window._trebbleAudioContext.resume() : RSVP.Promise.resolve();
    },

    getPermissionToRecord: function(recordingMediaType) {
        const mediaType = recordingMediaType === "video" ? "video" : this.getRecordingMediaType();
        if (window.waitForCordovaToLoad) {
            return this.getPermissionToRecordOnNative(mediaType);
        } else {
            return this.getPermissionToRecordOnWeb(mediaType);
        }
    },

    _getPermissionNamesForMediaType: function(recordingMediaType) {
        return recordingMediaType === "video" ? ["microphone", "camera"] : ["microphone"];
    },

    _hasPermissionToRecordInThisSession: function(recordingMediaType) {
        const mediaType = recordingMediaType === "video" ? "video" : "audio";
        const permissionState = this._userHasPermissionToRecordInThisSession || {};

        if (mediaType === "video") {
            return !!permissionState.video;
        }

        return !!(permissionState.audio || permissionState.video);
    },

    _markPermissionToRecordGrantedInThisSession: function(recordingMediaType) {
        const mediaType = recordingMediaType === "video" ? "video" : "audio";

        if (!this._userHasPermissionToRecordInThisSession || typeof this._userHasPermissionToRecordInThisSession !== "object") {
            this._userHasPermissionToRecordInThisSession = {
                audio: false,
                video: false
            };
        }

        if (mediaType === "video") {
            this._userHasPermissionToRecordInThisSession.video = true;
            this._userHasPermissionToRecordInThisSession.audio = true;
        } else {
            this._userHasPermissionToRecordInThisSession.audio = true;
        }
    },

    _queryWebPermissionState: function(permissionName) {
        if (!(navigator && navigator.permissions && navigator.permissions.query)) {
            return RSVP.Promise.resolve(null);
        }

        return navigator.permissions.query({ name: permissionName }).then(function(permissionStatus) {
            return permissionStatus ? permissionStatus.state : null;
        }).catch(function() {
            return null;
        });
    },

    _isWebRecordingPermissionAlreadyGranted: function(recordingMediaType) {
        const permissionNames = this._getPermissionNamesForMediaType(recordingMediaType);

        return RSVP.all(permissionNames.map((function(permissionName) {
            return this._queryWebPermissionState(permissionName);
        }).bind(this))).then(function(permissionStates) {
            const supportedPermissionStates = permissionStates.filter(function(permissionState) {
                return permissionState !== null && permissionState !== undefined;
            });

            return supportedPermissionStates.length > 0 && supportedPermissionStates.every(function(permissionState) {
                return permissionState === "granted";
            });
        });
    },

    getRecordingPermissionState: function(recordingMediaType) {
        const permissionNames = this._getPermissionNamesForMediaType(recordingMediaType);

        return RSVP.all(permissionNames.map((function(permissionName) {
            return this._queryWebPermissionState(permissionName);
        }).bind(this))).then(function(permissionStates) {
            const supportedPermissionStates = permissionStates.filter(function(permissionState) {
                return permissionState !== null && permissionState !== undefined;
            });

            if (!supportedPermissionStates.length) {
                return "unknown";
            }

            if (supportedPermissionStates.every(function(permissionState) {
                return permissionState === "granted";
            })) {
                return "granted";
            }

            if (supportedPermissionStates.some(function(permissionState) {
                return permissionState === "denied";
            })) {
                return "denied";
            }

            if (supportedPermissionStates.some(function(permissionState) {
                return permissionState === "prompt";
            })) {
                return "prompt";
            }

            return supportedPermissionStates[0] || "unknown";
        });
    },

    _enumerateAvailableInputDevicesForKind: function(kind) {
        if (!(navigator && navigator.mediaDevices && navigator.mediaDevices.enumerateDevices)) {
            return RSVP.Promise.resolve([]);
        }

        return navigator.mediaDevices.enumerateDevices().then(function(devices) {
            if (!devices) {
                return [];
            }

            let defaultDevice = null;
            const groupIdToDeviceArray = {};
            const inputDevices = devices.filter(function(device) {
                const matchingInput = device.kind === kind ? device : null;

                if (matchingInput) {
                    let deviceArray = groupIdToDeviceArray[matchingInput.groupId];
                    if (!deviceArray) {
                        deviceArray = [];
                        groupIdToDeviceArray[matchingInput.groupId] = deviceArray;
                    }
                    deviceArray.push(matchingInput);
                    if (matchingInput.deviceId === "default") {
                        defaultDevice = matchingInput;
                    }
                }

                return matchingInput;
            });

            if (defaultDevice && groupIdToDeviceArray[defaultDevice.groupId] && groupIdToDeviceArray[defaultDevice.groupId].length > 1) {
                inputDevices.splice(inputDevices.indexOf(defaultDevice), 1);
            }

            return inputDevices;
        });
    },

    _doesInputDeviceListHaveVisibleLabels: function(inputDevices) {
        return !!(inputDevices && inputDevices.length > 0 && inputDevices.some(hasVisibleDeviceLabel));
    },

    _getDevicesAvailableForKind : function(kind, mediaTypeForPermissionProbe){
        return this._enumerateAvailableInputDevicesForKind(kind).then((function(inputDevices) {
            if (this._doesInputDeviceListHaveVisibleLabels(inputDevices)) {
                return inputDevices;
            }

            return this.getPermissionToRecord(mediaTypeForPermissionProbe).then((function() {
                return this._enumerateAvailableInputDevicesForKind(kind);
            }).bind(this));
        }).bind(this));
    },

    getAudioDevicesAvailable : function(){
        return this._getDevicesAvailableForKind("audioinput", "audio");
    },

    getVideoDevicesAvailable : function(){
        return this._getDevicesAvailableForKind("videoinput", "video");
    },

    getAudioDevicesAvailableWithoutPrompt: function() {
        return this._enumerateAvailableInputDevicesForKind("audioinput");
    },

    getVideoDevicesAvailableWithoutPrompt: function() {
        return this._enumerateAvailableInputDevicesForKind("videoinput");
    },

    getAudioOutputDevicesAvailableWithoutPrompt: function() {
        return this._enumerateAvailableInputDevicesForKind("audiooutput");
    },

    isSpeakerOutputSelectionSupported: function() {
        return !!(
            window.isSecureContext &&
            typeof HTMLMediaElement !== "undefined" &&
            HTMLMediaElement.prototype &&
            typeof HTMLMediaElement.prototype.setSinkId === "function"
        );
    },

    doInputDevicesHaveVisibleLabels: function(inputDevices) {
        return this._doesInputDeviceListHaveVisibleLabels(inputDevices);
    },

    getPermissionToRecordOnWeb: function(recordingMediaType) {
        const mediaType = recordingMediaType === "video" ? "video" : "audio";

        if (this._hasPermissionToRecordInThisSession(mediaType)) {
            return RSVP.Promise.resolve(true);
        }

        return this._isWebRecordingPermissionAlreadyGranted(mediaType).then((function(permissionAlreadyGranted) {
            if (permissionAlreadyGranted) {
                this._markPermissionToRecordGrantedInThisSession(mediaType);
                return true;
            }

        let waitingForPermission = true;
        let popupMessageViewPromise = null;
        const showWaitPopupIfApplicable = function() {
            if (waitingForPermission && !Utils.getInstance().isJQMPopupOpen()) {
                //show wait popup
                const message = window.getI18n(ti18n, "WE_ARE_WAITING_FOR_PERMISSION_TO_RECORD", {
                    inputLabel: this._getMediaCaptureInputLabel(mediaType)
                });
                const subMessage = window.isMobileBrowser ? window.getI18n(ti18n, "PLEASE_FOLLOW_PHONE_INSTRUTION_FOR_PERMISSION") : window.getI18n(ti18n, "PLEASE_FOLLOW_BROWSER_INSTRUTION_FOR_PERMISSION");
                popupMessageViewPromise = PersistentModels.getInstance().getRouter().showMessagePopup(message, subMessage);
            }
        }.bind(this);
        const timeToWaitBeforeShowingWaitPopupInMillisec = 3000;

        setTimeout(showWaitPopupIfApplicable, timeToWaitBeforeShowingWaitPopupInMillisec);
        // This throwaway stream only triggers the permission prompt and targets the default device,
        // so it must not be signal-validated: the user's selected microphone might work even when
        // the default one produces no audio.
        return this._getBrowserMediaStream(mediaType, { includePreferredDeviceId: false, skipAudioSignalValidation: true }).then(function(result) {
            waitingForPermission = false;
            this._markPermissionToRecordGrantedInThisSession(mediaType);
            if (popupMessageViewPromise) {
                popupMessageViewPromise.then(function(popupView) {
                    popupView.close()
                })
            }
            stopStreamTracks(result);
            return true;

        }.bind(this)).catch(function(error) {
            waitingForPermission = false;
            if (popupMessageViewPromise) {
                popupMessageViewPromise.then(function(popupView) {
                    popupView.close();
                });
            }
            throw error;
        });
        }).bind(this));
    },

    getPermissionToRecordOnNative: function(recordingMediaType) {
        //return RSVP.Promise.resolve();

        const mediaType = recordingMediaType === "video" ? "video" : this.getRecordingMediaType();
        if (mediaType === "video") {
            return RSVP.Promise.reject(new Error(window.getI18n(ti18n, "RECORDING_IS_NOT_SUPPORTED") || NATIVE_VIDEO_RECORDING_UNSUPPORTED_ERROR));
        }

        return new RSVP.Promise((function(resolve, reject) {
            if (this._hasPermissionToRecordInThisSession(mediaType)) {
                return resolve(true);
            } else {
                let waitingForPermission = true;
                let popupMessageViewPromise = null;
                const showWaitPopupIfApplicable = function() {
                    if (waitingForPermission) {
                        //show wait popup
                        const message = window.getI18n(ti18n, "WE_ARE_WAITING_FOR_PERMISSION_TO_RECORD", {
                            inputLabel: this._getMediaCaptureInputLabel(mediaType)
                        });
                        const subMessage = window.isMobileBrowser ? window.getI18n(ti18n, "PLEASE_FOLLOW_PHONE_INSTRUTION_FOR_PERMISSION") : window.getI18n(ti18n, "PLEASE_FOLLOW_BROWSER_INSTRUTION_FOR_PERMISSION");
                        popupMessageViewPromise = PersistentModels.getInstance().getRouter().showMessagePopup(message, subMessage);
                    }
                }.bind(this);
                const timeToWaitBeforeShowingWaitPopupInMillisec = 3000;
                setTimeout(showWaitPopupIfApplicable, timeToWaitBeforeShowingWaitPopupInMillisec);

                this._recordingPromise = FileUploadHelper.getInstance().getPersistentFileSystemUrl().then((function(persitentFSUrl) {
                    const fileExtension = window.isIOSMobileDevice ? "wav" : "aac";
                    const fileName = Utils.getInstance().generateRandomId() + "." + fileExtension;
                    const filePrefix = persitentFSUrl;
                    const audioFileName = filePrefix + fileName;
                    const mediaRec = new window.Media(audioFileName,
                        function() {

                        },
                        function(err) {
                            waitingForPermission = false;
                            console.error(err);
                            reject(err);
                        }, (function(status) {
                            if (waitingForPermission && status == 2) {
                                waitingForPermission = false;
                                this._markPermissionToRecordGrantedInThisSession(mediaType);
                                if (popupMessageViewPromise) {
                                    popupMessageViewPromise.then(function(popupView) {
                                        popupView.close()
                                    })
                                }
                                resolve();
                                mediaRec.stopRecord();
                                mediaRec.release();
                                FileUploadHelper.getInstance().deleteFileFromPersistentFileSystemUrl(fileName);
                            }
                        }).bind(this)
                        );
                    mediaRec.startRecord();
                }).bind(this));
            }
        }).bind(this))
    },


    _createNewAudioContext: function() {
        window.AudioContext = window.AudioContext || window.webkitAudioContext;
        window._trebbleAudioContext = new AudioContext();
        unmute(window._trebbleAudioContext);
        window._trebbleAudioContext._processorConfig = this._getRecorderProcessorDefaultConfig();
        window._trebbleAudioContext._processor = (window._trebbleAudioContext.createScriptProcessor ||
            window._trebbleAudioContext.createJavaScriptNode).call(window._trebbleAudioContext,
            window._trebbleAudioContext._processorConfig.bufferLen, window._trebbleAudioContext._processorConfig.numChannels, window._trebbleAudioContext._processorConfig.numChannels);
            window._trebbleAudioContext._processor.connect(window._trebbleAudioContext.destination);
            window._trebbleAudioContext.nodeAlreadyConnected = true;
        },

        _closeAudioContext: function() {
            if (window._trebbleAudioContext) {
                window._trebbleAudioContext.close();
                window._trebbleAudioContext._processor.disconnect();
                window._trebbleAudioContext = null;
            }
        },

        isDurableBrowserRecordingDraftsEnabled: function() {
            const draftStore = BrowserRecordingDraftStore.getInstance();
            return !!(draftStore && draftStore.isEnabled && draftStore.isEnabled());
        },

        canAccessDurableBrowserRecordingDrafts: function() {
            const draftStore = BrowserRecordingDraftStore.getInstance();
            return !!(draftStore && (
                (draftStore.canAccessPersistedDrafts && draftStore.canAccessPersistedDrafts()) ||
                (draftStore.isEnabled && draftStore.isEnabled())
            ));
        },

        _registerDurableDraftManifest: function(fileName, draftManifest) {
            if (!fileName) {
                return null;
            }

            if (draftManifest && draftManifest.recordingId) {
                this._clearDurableDraftManifest(draftManifest.recordingId);
            }

            if (draftManifest) {
                this._recordFilenameToDurableDraftManifest[fileName] = draftManifest;
            } else {
                delete this._recordFilenameToDurableDraftManifest[fileName];
            }

            return draftManifest || null;
        },

        registerDurableDraftManifest: function(fileName, draftManifest) {
            return this._registerDurableDraftManifest(fileName, draftManifest);
        },

        _clearDurableDraftManifest: function(fileNameOrRecordingId) {
            if (!fileNameOrRecordingId) {
                return;
            }

            Object.keys(this._recordFilenameToDurableDraftManifest || {}).forEach((function(trackedKey) {
                const trackedDraftManifest = this._recordFilenameToDurableDraftManifest[trackedKey];
                if (trackedKey === fileNameOrRecordingId || (trackedDraftManifest && trackedDraftManifest.recordingId === fileNameOrRecordingId)) {
                    delete this._recordFilenameToDurableDraftManifest[trackedKey];
                }
            }).bind(this));
        },

        getDurableDraftManifestForRecording: function(fileName) {
            return fileName ? this._recordFilenameToDurableDraftManifest[fileName] || null : null;
        },

        getRecoverableRecordingDraftsForCurrentUser: function(includeDismissed) {
            if (!this.canAccessDurableBrowserRecordingDrafts()) {
                return RSVP.Promise.resolve([]);
            }

            return RSVP.Promise.resolve(
                BrowserRecordingDraftStore.getInstance().listRecoverableDraftsForCurrentUser({
                    includeDismissed: includeDismissed !== false
                })
            );
        },

        getRecordingDraftById: function(recordingId) {
            if (!this.canAccessDurableBrowserRecordingDrafts() || !recordingId) {
                return RSVP.Promise.resolve(null);
            }

            const draftStore = BrowserRecordingDraftStore.getInstance();
            const getDraftForCurrentUser = draftStore.getDraftForCurrentUser || draftStore.getDraft;
            return RSVP.Promise.resolve(getDraftForCurrentUser.call(draftStore, recordingId));
        },

        _buildRecoveredDraftRecorderInfo: function(draftManifest) {
            const recordingStartTime = draftManifest && draftManifest.startedAt ? draftManifest.startedAt : Date.now();
            const durationInMilli = draftManifest && draftManifest.durationMs ? draftManifest.durationMs : 0;
            const recordingEndTime = durationInMilli > 0 ? recordingStartTime + durationInMilli : recordingStartTime;
            const mimeType = draftManifest && draftManifest.mimeType ? draftManifest.mimeType : "application/octet-stream";
            const mediaType = mimeType.indexOf("video/") === 0 ? "video" : "audio";

            return {
                getMimeType: function() {
                    return mimeType;
                },
                getMediaType: function() {
                    return mediaType;
                },
                getRecordingHealth: function() {
                    return {
                        startedAt: recordingStartTime,
                        endedAt: recordingEndTime,
                        bytesStored: draftManifest && draftManifest.bytesStored ? draftManifest.bytesStored : 0,
                        storageBackend: draftManifest && draftManifest.storageBackend ? draftManifest.storageBackend : null
                    };
                }
            };
        },

        recoverPersistedRecordingDraft: function(recordingId) {
            if (!this.canAccessDurableBrowserRecordingDrafts() || !recordingId) {
                return RSVP.Promise.resolve(null);
            }

            const draftStore = BrowserRecordingDraftStore.getInstance();
            const getDraftForCurrentUser = draftStore.getDraftForCurrentUser || draftStore.getDraft;
            return RSVP.Promise.resolve(getDraftForCurrentUser.call(draftStore, recordingId)).then((function(draftManifest) {
                if (!draftManifest) {
                    return null;
                }

                const fileName = draftManifest.fileName || recordingId;
                return RSVP.Promise.resolve(BrowserRecordingDraftStore.getInstance().getPersistedBinaryFile(recordingId, draftManifest)).then((function(recordedFileBlob) {
                    const hasValidRecordedFileBlob = !!(recordedFileBlob && typeof recordedFileBlob.size === "number" && (
                        typeof recordedFileBlob.arrayBuffer === "function" ||
                        typeof recordedFileBlob.slice === "function"
                    ));
                    if (!hasValidRecordedFileBlob) {
                        this._registerDurableDraftManifest(fileName, draftManifest);
                        return {
                            fileName: fileName,
                            draftManifest: draftManifest,
                            mediaInfo: null
                        };
                    }

                    const recoveredRecorderInfo = this._buildRecoveredDraftRecorderInfo(draftManifest);
                    return this._buildRecordedMediaInfo(fileName, recordedFileBlob, recoveredRecorderInfo).then((function(mediaInfo) {
                        this._registerDurableDraftManifest(fileName, draftManifest);
                        return {
                            fileName: fileName,
                            draftManifest: draftManifest,
                            mediaInfo: mediaInfo
                        };
                    }).bind(this));
                }).bind(this));
            }).bind(this));
        },

        releaseRecording: function(fileName) {
            if (!fileName) {
                return RSVP.Promise.resolve();
            }

            const revokeLocalUrlIfApplicable = (function() {
                const mediaInfo = this._recordFilenameToMediaInfo[fileName];
                if (mediaInfo && mediaInfo.localUrl) {
                    try {
                        URL.revokeObjectURL(mediaInfo.localUrl);
                    } catch (_) {
                        // Ignore revoke failures while releasing runtime state.
                    }
                }
            }).bind(this);

            const waitForRecordingPromise = (this._recordingPromise ? this._recordingPromise : RSVP.Promise.resolve()).catch(function() {
                return null;
            });

            return waitForRecordingPromise.then((function() {
                const recorderObj = this._recordMediaObjects[fileName];
                if (recorderObj && recorderObj.release) {
                    recorderObj.release();
                } else if (recorderObj && recorderObj.clear) {
                    recorderObj.clear();
                }
                revokeLocalUrlIfApplicable();
                delete this._recordMediaObjects[fileName];
                delete this._recordMediaFilenames[fileName];
                delete this._recordFilenameToBlob[fileName];
                delete this._recordFilenameToMediaInfo[fileName];
            }).bind(this));
        },

        releaseAllRecordings: function() {
            const trackedRecordingNames = Object.keys(this._recordMediaObjects || {});
            if (trackedRecordingNames.length === 0 && Object.keys(this._recordFilenameToMediaInfo || {}).length === 0) {
                return RSVP.Promise.resolve();
            }

            const allRecordingNames = trackedRecordingNames.concat(Object.keys(this._recordFilenameToMediaInfo || {})).filter(function(fileName, index, arrayOfFileNames) {
                return arrayOfFileNames.indexOf(fileName) === index;
            });

            return RSVP.Promise.all(allRecordingNames.map((function(fileName) {
                return this.releaseRecording(fileName).catch(function(error) {
                    console.warn("Unable to release one of the browser recordings without deleting it", error);
                    return null;
                });
            }).bind(this)));
        },

        deletePersistedRecordingDraft: function(recordingId) {
            if (!recordingId) {
                return RSVP.Promise.resolve();
            }

            const finalizeDeletion = (function() {
                this._clearDurableDraftManifest(recordingId);
            }).bind(this);

            if (!this.canAccessDurableBrowserRecordingDrafts()) {
                finalizeDeletion();
                return RSVP.Promise.resolve();
            }

            const draftStore = BrowserRecordingDraftStore.getInstance();
            const getDraftForCurrentUser = draftStore.getDraftForCurrentUser || draftStore.getDraft;
            return RSVP.Promise.resolve(getDraftForCurrentUser.call(draftStore, recordingId)).then((function(draftManifest) {
                if (!draftManifest) {
                    finalizeDeletion();
                    return null;
                }

                return draftStore.deletePersistedBinary(recordingId, {
                    patchManifest: false
                }).then(function() {
                    return draftStore.markDiscarded(recordingId);
                });
            }).bind(this)).then((function() {
                finalizeDeletion();
            }).bind(this));
        },

        startRecording: function(fileName, options) {
            this._isRecording = true;
            const recordingOptions = options || {};
            const recordingMediaType = recordingOptions.mediaType === "video" ? "video" : this.getRecordingMediaType();
            const existingStream = recordingOptions.existingStream || null;
            this.setRecordingMediaType(recordingMediaType);

            if (window.device && window.device.platform == "browser" && !(window.trebble.useNativeRecording && "Media" in window)) {

                this._recordingPromise = this.getBrowserRecorder(fileName, recordingMediaType, existingStream, recordingOptions).then((function(recorderObj) {
                    return RSVP.Promise.resolve().then(function() {
                        return recorderObj.record();
                    }).then((function() {
                        this._recordMediaObjects[fileName] = recorderObj;
                        return recorderObj;
                    }).bind(this)).catch((function(error) {
                        this._releaseBrowserRecorderAfterStartFailure(recorderObj);
                        throw error;
                    }).bind(this));
                }).bind(this)).catch((function(error) {
                    this._isRecording = false;
                    this._recordingPromise = null;
                    throw error;
                }).bind(this));
                return this._recordingPromise;

            } else {
                if (recordingMediaType === "video") {
                    this._isRecording = false;
                    return RSVP.Promise.reject(new Error(window.getI18n(ti18n, "RECORDING_IS_NOT_SUPPORTED") || NATIVE_VIDEO_RECORDING_UNSUPPORTED_ERROR));
                }
                this._recordingPromise = FileUploadHelper.getInstance().getPersistentFileSystemUrl().then((function(persitentFSUrl) {
                    const filePrefix = persitentFSUrl;
                    const mediaRec = new window.Media(filePrefix + fileName,
                        function() {

                        },
                        function() {

                        }
                    );
                    mediaRec.startRecord();
                    this._recordMediaObjects[fileName] = mediaRec;
                    this._recordMediaFilenames[fileName] = filePrefix + fileName;
                }).bind(this));
                return this._recordingPromise;
            }
        },

        getBrowserRecorderAndStartRecordingForiOSWeb: function(fileName, options) {
            this._isRecording = true;
            const recordingOptions = options || {};
            const recordingMediaType = recordingOptions.mediaType === "video" ? "video" : this.getRecordingMediaType();
            const existingStream = recordingOptions.existingStream || null;
            this.setRecordingMediaType(recordingMediaType);

            if (window.device && window.device.platform == "browser" && !(window.trebble.useNativeRecording && "Media" in window)) {

                this._recordingPromise = this.getBrowserRecorderAndStartRecord(fileName, recordingMediaType, existingStream, recordingOptions).catch((function(error) {
                    this._isRecording = false;
                    this._recordingPromise = null;
                    throw error;
                }).bind(this));
                return this._recordingPromise;

            } else {
                if (recordingMediaType === "video") {
                    this._isRecording = false;
                    return RSVP.Promise.reject(new Error(window.getI18n(ti18n, "RECORDING_IS_NOT_SUPPORTED") || NATIVE_VIDEO_RECORDING_UNSUPPORTED_ERROR));
                }
                this._recordingPromise = FileUploadHelper.getInstance().getPersistentFileSystemUrl().then((function(persitentFSUrl) {
                    const filePrefix = persitentFSUrl;
                    const mediaRec = new window.Media(filePrefix + fileName,
                        function() {

                        },
                        function() {

                        }
                    );
                    mediaRec.startRecord();
                    this._recordMediaObjects[fileName] = mediaRec;
                    this._recordMediaFilenames[fileName] = filePrefix + fileName;
                }).bind(this));
                return this._recordingPromise;
            }
        },

        isAudioContextCreated: function() {
            return !!window._trebbleAudioContext;
        },


    getAudioContext : function(){
          if (!window._trebbleAudioContext) {
            this._createNewAudioContext();
        }
        return window._trebbleAudioContext;
    },

    _buildBrowserRecordingConstraints: function(recordingMediaType, options) {
        const mediaType = recordingMediaType === "video" ? "video" : "audio";
        const constraints = {
            audio: buildAudioConstraints(options)
        };

        if (mediaType === "video") {
            constraints.video = buildVideoConstraints(options);
        }

        return constraints;
    },

    _buildBrowserPreviewConstraints: function() {
        return {
            audio: false,
            video: buildVideoConstraints()
        };
    },

    isScreenShareSupported: function() {
        return !!(navigator && navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia);
    },

    _getScreenPreviewConstraints: function() {
        return {
            video: true,
            audio: false
        };
    },

    _getPermissionNamesForConstraints: function(constraints) {
        const permissionNames = [];

        if (constraints && constraints.audio) {
            permissionNames.push("microphone");
        }

        if (constraints && constraints.video) {
            permissionNames.push("camera");
        }

        return permissionNames;
    },

    _getMediaTypeForConstraints: function(constraints) {
        return constraints && constraints.video ? "video" : "audio";
    },

    _shouldApplyTimeoutToGetUserMediaRequest: function(constraints) {
        const mediaType = this._getMediaTypeForConstraints(constraints);
        const permissionNames = this._getPermissionNamesForConstraints(constraints);

        if (!permissionNames.length) {
            return RSVP.Promise.resolve({
                shouldApplyTimeout: true,
                timeoutInMilli: USER_MEDIA_REQUEST_TIMEOUT_IN_MILLI
            });
        }

        return RSVP.all(permissionNames.map((function(permissionName) {
            return this._queryWebPermissionState(permissionName);
        }).bind(this))).then(function(permissionStates) {
            const supportedPermissionStates = permissionStates.filter(function(permissionState) {
                return permissionState !== null && permissionState !== undefined;
            });

            if (supportedPermissionStates.length !== permissionNames.length) {
                return {
                    shouldApplyTimeout: true,
                    timeoutInMilli: this._hasPermissionToRecordInThisSession(mediaType) ? USER_MEDIA_REQUEST_TIMEOUT_IN_MILLI : USER_MEDIA_REQUEST_TIMEOUT_WHEN_PERMISSION_STATE_IS_UNKNOWN_IN_MILLI
                };
            }

            if (supportedPermissionStates.some(function(permissionState) {
                return permissionState === "prompt";
            })) {
                return {
                    shouldApplyTimeout: false
                };
            }

            return {
                shouldApplyTimeout: supportedPermissionStates.every(function(permissionState) {
                    return permissionState === "granted";
                }),
                timeoutInMilli: USER_MEDIA_REQUEST_TIMEOUT_IN_MILLI
            };
        }.bind(this));
    },

    _withTimeout: function(promise, timeoutInMilli, timeoutErrorMessage, onLateResolve) {
        const timeout = timeoutInMilli || USER_MEDIA_REQUEST_TIMEOUT_IN_MILLI;

        return new RSVP.Promise(function(resolve, reject) {
            let didTimeout = false;
            const timeoutId = setTimeout(function() {
                didTimeout = true;
                reject(buildTimeoutError(timeoutErrorMessage));
            }, timeout);

            RSVP.Promise.resolve(promise).then(function(result) {
                if (didTimeout) {
                    if (onLateResolve) {
                        try {
                            onLateResolve(result);
                        } catch (_) {
                            // Ignore cleanup failures for late media results.
                        }
                    }
                    return;
                }

                clearTimeout(timeoutId);
                resolve(result);
            }).catch(function(error) {
                if (didTimeout) {
                    return;
                }

                clearTimeout(timeoutId);
                reject(error);
            });
        });
    },

    _validateVideoStreamCanRender: function(stream) {
        return new RSVP.Promise(function(resolve, reject) {
            const validationVideoEl = document.createElement("video");
            let completed = false;

            const completeValidation = function(error) {
                if (completed) {
                    return;
                }

                completed = true;
                clearTimeout(validationTimeoutId);
                validationVideoEl.onloadedmetadata = null;
                validationVideoEl.oncanplay = null;
                validationVideoEl.onplaying = null;

                try {
                    validationVideoEl.pause();
                } catch (_) {
                    // Ignore cleanup failures for off-DOM validation video.
                }

                try {
                    validationVideoEl.srcObject = null;
                } catch (_) {
                    // Ignore cleanup failures for off-DOM validation video.
                }

                if (error) {
                    reject(error);
                } else {
                    resolve(stream);
                }
            };

            const onVideoReady = function() {
                if (validationVideoEl.videoWidth > 0 || validationVideoEl.videoHeight > 0) {
                    completeValidation();
                }
            };

            const validationTimeoutId = setTimeout(function() {
                completeValidation(buildTimeoutError("Timed out while validating the camera stream."));
            }, VIDEO_STREAM_VALIDATION_TIMEOUT_IN_MILLI);

            validationVideoEl.muted = true;
            validationVideoEl.autoplay = true;
            validationVideoEl.playsInline = true;
            validationVideoEl.onloadedmetadata = onVideoReady;
            validationVideoEl.oncanplay = onVideoReady;
            validationVideoEl.onplaying = onVideoReady;

            try {
                validationVideoEl.srcObject = stream;
                const playPromise = validationVideoEl.play ? validationVideoEl.play() : null;
                if (playPromise && playPromise.catch) {
                    playPromise.catch(function() {
                        // Validation still succeeds if metadata becomes available without autoplay.
                    });
                }
            } catch (error) {
                completeValidation(error);
            }
        });
    },

    _validateBrowserStream: function(stream, validationOptions) {
        const options = validationOptions || {};
        const audioTracks = stream && stream.getAudioTracks ? stream.getAudioTracks() : [];
        const videoTracks = stream && stream.getVideoTracks ? stream.getVideoTracks() : [];

        if (options.requireAudio && !(audioTracks.length > 0 && audioTracks.some(isTrackLive))) {
            return RSVP.Promise.reject(new Error("No live audio track was made available."));
        }

        if (options.requireVideo && !(videoTracks.length > 0 && videoTracks.some(isTrackLive))) {
            return RSVP.Promise.reject(new Error("No live video track was made available."));
        }

        const audioSignalValidationPromise = options.requireAudio && options.validateAudioSignal !== false ? this._validateAudioStreamProducesSignal(stream) : RSVP.Promise.resolve(stream);

        if (options.requireVideo) {
            return RSVP.Promise.all([
                audioSignalValidationPromise,
                this._validateVideoStreamCanRender(stream).then((function() {
                    return this._validateVideoStreamAspectRatio(stream, options);
                }).bind(this))
            ]).then(function() {
                return stream;
            });
        }

        return audioSignalValidationPromise;
    },

    _validateAudioStreamProducesSignal: function(stream) {
        if (!VALIDATE_MIC_SIGNAL_BEFORE_RECORDING) {
            return RSVP.Promise.resolve(stream);
        }

        return probeAudioStreamForSignal(stream);
    },

    _validateVideoStreamAspectRatio: function(stream, validationOptions) {
        const options = validationOptions || {};
        const preferredAspectRatio = Number(options.preferredAspectRatio);
        if (!(preferredAspectRatio > 0)) {
            return RSVP.Promise.resolve(stream);
        }

        const videoTrack = stream && stream.getVideoTracks ? stream.getVideoTracks()[0] : null;
        if (!videoTrack) {
            return RSVP.Promise.resolve(stream);
        }

        const trackSettings = videoTrack.getSettings ? videoTrack.getSettings() : null;
        const resolvedWidth = trackSettings && trackSettings.width ? Number(trackSettings.width) : 0;
        const resolvedHeight = trackSettings && trackSettings.height ? Number(trackSettings.height) : 0;
        const resolvedAspectRatioFromDimensions = (resolvedWidth > 0 && resolvedHeight > 0) ? (resolvedWidth / resolvedHeight) : 0;
        const resolvedAspectRatio = resolvedAspectRatioFromDimensions || (trackSettings && trackSettings.aspectRatio ? Number(trackSettings.aspectRatio) : 0);

        if (!(resolvedAspectRatio > 0)) {
            return RSVP.Promise.resolve(stream);
        }

        const aspectRatioTolerance = Number(options.aspectRatioTolerance);
        const toleranceToUse = aspectRatioTolerance > 0 ? aspectRatioTolerance : PREFERRED_WIDESCREEN_VIDEO_ASPECT_RATIO_TOLERANCE;
        if (Math.abs(resolvedAspectRatio - preferredAspectRatio) <= toleranceToUse) {
            return RSVP.Promise.resolve(stream);
        }

        const error = new Error("The camera selected a non-widescreen preview mode.");
        error.name = "OverconstrainedError";
        error.preferredAspectRatio = preferredAspectRatio;
        error.resolvedAspectRatio = resolvedAspectRatio;
        error.trackSettings = trackSettings || null;
        return RSVP.Promise.reject(error);
    },

    _getUserMediaWithTimeout: function(constraints, timeoutInMilli) {
        if (!(navigator && navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
            return RSVP.Promise.reject(new Error(window.getI18n(ti18n, "RECORDING_IS_NOT_SUPPORTED")));
        }

        return this._shouldApplyTimeoutToGetUserMediaRequest(constraints).then((function(timeoutConfig) {
            const getUserMediaPromise = navigator.mediaDevices.getUserMedia(constraints);
            const resolvedTimeoutConfig = timeoutConfig || {};
            const shouldApplyTimeout = !!resolvedTimeoutConfig.shouldApplyTimeout;
            const timeoutToUse = resolvedTimeoutConfig.timeoutInMilli || timeoutInMilli;

            if (!shouldApplyTimeout) {
                return getUserMediaPromise;
            }

            return this._withTimeout(
                getUserMediaPromise,
                timeoutToUse,
                "Timed out while starting media devices.",
                function(stream) {
                    stopStreamTracks(stream);
                }
            );
        }).bind(this));
    },

    _buildBrowserRecordingConstraintCandidates: function(recordingMediaType, options) {
        const mediaType = recordingMediaType === "video" ? "video" : "audio";
        const streamOptions = options || {};
        const constraintCandidates = [];

        if (mediaType === "video") {
            buildVideoResolutionCandidateList(LocalStorageHelper.getInstance().getRecordingVideoResolution()).forEach(function(videoResolution) {
                constraintCandidates.push({
                    constraints: {
                        audio: buildAudioConstraints(streamOptions),
                        video: buildVideoConstraintsForResolution(videoResolution, streamOptions)
                    },
                    validationOptions: buildVideoValidationOptionsForResolution(videoResolution)
                });
            });
        } else {
            constraintCandidates.push(this._buildBrowserRecordingConstraints(mediaType, streamOptions));
        }

        if (streamOptions.includePreferredDeviceId !== false) {
            const fallbackOptions = Object.assign({}, streamOptions, { includePreferredDeviceId: false });
            if (mediaType === "video") {
                buildVideoResolutionCandidateList(LocalStorageHelper.getInstance().getRecordingVideoResolution()).forEach(function(videoResolution) {
                    constraintCandidates.push({
                        constraints: {
                            audio: buildAudioConstraints(fallbackOptions),
                            video: buildVideoConstraintsForResolution(videoResolution, fallbackOptions)
                        },
                        validationOptions: buildVideoValidationOptionsForResolution(videoResolution)
                    });
                });
            } else {
                constraintCandidates.push(this._buildBrowserRecordingConstraints(mediaType, fallbackOptions));
            }
        }

        if (mediaType === "video") {
            constraintCandidates.push({
                audio: buildAudioConstraints(streamOptions),
                video: true
            });
            constraintCandidates.push({
                audio: buildAudioConstraints({ includePreferredDeviceId: false }),
                video: true
            });
        }

        return dedupeMediaRequestCandidates(constraintCandidates);
    },

    _buildBrowserPreviewConstraintCandidates: function() {
        const preferredVideoResolution = LocalStorageHelper.getInstance().getRecordingVideoResolution();
        const constraintCandidates = [];

        buildVideoResolutionCandidateList(preferredVideoResolution).forEach(function(videoResolution) {
            constraintCandidates.push({
                constraints: {
                    audio: false,
                    video: buildVideoConstraintsForResolution(videoResolution, { previewOnly: true })
                },
                validationOptions: buildVideoValidationOptionsForResolution(videoResolution)
            });
        });

        buildVideoResolutionCandidateList(preferredVideoResolution).forEach(function(videoResolution) {
            constraintCandidates.push({
                constraints: {
                    audio: false,
                    video: buildVideoConstraintsForResolution(videoResolution, { includePreferredDeviceId: false, previewOnly: true })
                },
                validationOptions: buildVideoValidationOptionsForResolution(videoResolution)
            });
        });

        constraintCandidates.push({
            audio: false,
            video: true
        });

        return dedupeMediaRequestCandidates(constraintCandidates);
    },

    _acquireBrowserMediaStreamWithFallback: function(constraintCandidates, validationOptions, timeoutInMilli) {
        const uniqueConstraintCandidates = dedupeMediaRequestCandidates(constraintCandidates);
        const defaultValidationOptions = validationOptions || {};

        const tryAcquireCandidate = (function(index, lastError) {
            if (index >= uniqueConstraintCandidates.length) {
                return RSVP.Promise.reject(lastError || buildTimeoutError("Timed out while starting media input."));
            }

            const candidate = uniqueConstraintCandidates[index];
            const constraints = candidate && candidate.constraints ? candidate.constraints : candidate;
            const candidateValidationOptions = Object.assign({}, defaultValidationOptions, candidate && candidate.validationOptions ? candidate.validationOptions : {});
            return this._getUserMediaWithTimeout(constraints, timeoutInMilli).then((function(stream) {
                return this._validateBrowserStream(stream, candidateValidationOptions).then(function() {
                    return stream;
                }).catch(function(error) {
                    stopStreamTracks(stream);
                    throw error;
                });
            }).bind(this)).catch((function(error) {
                const hasMoreCandidates = index < uniqueConstraintCandidates.length - 1;
                if (hasMoreCandidates && isRecoverableUserMediaError(error)) {
                    console.warn("Retrying media acquisition with fallback constraints", error);
                    return tryAcquireCandidate(index + 1, error);
                }

                throw error;
            }).bind(this));
        }).bind(this);

        return tryAcquireCandidate(0, null);
    },

    _getBrowserMediaStream: function(recordingMediaType, options) {
        const mediaType = recordingMediaType === "video" ? "video" : "audio";
        const streamOptions = options || {};
        const constraintCandidates = this._buildBrowserRecordingConstraintCandidates(mediaType, streamOptions);

        return this._acquireBrowserMediaStreamWithFallback(constraintCandidates, {
            requireAudio: true,
            requireVideo: mediaType === "video",
            validateAudioSignal: streamOptions.skipAudioSignalValidation !== true
        }, USER_MEDIA_REQUEST_TIMEOUT_IN_MILLI);
    },

    _getBrowserVideoPreviewStream: function() {
        return this._acquireBrowserMediaStreamWithFallback(this._buildBrowserPreviewConstraintCandidates(), {
            requireAudio: false,
            requireVideo: true
        }, USER_MEDIA_REQUEST_TIMEOUT_IN_MILLI);
    },

    _getBrowserCameraRecordingSourceStream: function() {
        const preferredVideoResolution = LocalStorageHelper.getInstance().getRecordingVideoResolution();
        const constraintCandidates = [];

        buildVideoResolutionCandidateList(preferredVideoResolution).forEach(function(videoResolution) {
            constraintCandidates.push({
                constraints: {
                    audio: false,
                    video: buildVideoConstraintsForResolution(videoResolution)
                },
                validationOptions: buildVideoValidationOptionsForResolution(videoResolution)
            });
        });

        buildVideoResolutionCandidateList(preferredVideoResolution).forEach(function(videoResolution) {
            constraintCandidates.push({
                constraints: {
                    audio: false,
                    video: buildVideoConstraintsForResolution(videoResolution, { includePreferredDeviceId: false })
                },
                validationOptions: buildVideoValidationOptionsForResolution(videoResolution)
            });
        });

        constraintCandidates.push({
            audio: false,
            video: true
        });

        return this._acquireBrowserMediaStreamWithFallback(constraintCandidates, {
            requireAudio: false,
            requireVideo: true
        }, USER_MEDIA_REQUEST_TIMEOUT_IN_MILLI);
    },

    _getBrowserScreenPreviewStream: function() {
        if (!this.isScreenShareSupported()) {
            return RSVP.Promise.reject(new Error("Screen sharing is not supported in this browser."));
        }

        return RSVP.Promise.resolve(navigator.mediaDevices.getDisplayMedia(this._getScreenPreviewConstraints())).then((function(stream) {
            return this._validateBrowserStream(stream, {
                requireAudio: false,
                requireVideo: true
            }).then(function() {
                return stream;
            }).catch(function(error) {
                stopStreamTracks(stream);
                throw error;
            });
        }).bind(this));
    },

    _getPreviewStreamForSourceType: function(sourceType) {
        return sourceType === "screen" ? this._getBrowserScreenPreviewStream() : this._getBrowserVideoPreviewStream();
    },

    getVideoPreviewStream: function(forceRefresh, options) {
        const previewOptions = options || {};
        const previewSourceType = previewOptions.sourceType === "screen" ? "screen" : "camera";
        const isExistingPreviewReusable = !forceRefresh && isStreamActive(this._videoPreviewStream) && this._videoPreviewSourceType === previewSourceType;

        if (isExistingPreviewReusable) {
            return RSVP.Promise.resolve(this._videoPreviewStream);
        }

        if (!forceRefresh && this._videoPreviewPromise && this._videoPreviewPendingSourceType === previewSourceType) {
            return this._videoPreviewPromise;
        }

        const shouldStopPreviousPreview = forceRefresh || (!!this._videoPreviewSourceType && this._videoPreviewSourceType !== previewSourceType);
        const waitForPreviousPreviewStopPromise = shouldStopPreviousPreview ? this.stopVideoPreview() : (this._videoPreviewStopPromise || RSVP.Promise.resolve());
        const previewRequestId = ++this._videoPreviewRequestId;
        this._videoPreviewPendingSourceType = previewSourceType;
        this._videoPreviewPromise = waitForPreviousPreviewStopPromise.then((function() {
            return this._getPreviewStreamForSourceType(previewSourceType);
        }).bind(this)).then((function(stream) {
            if (previewRequestId !== this._videoPreviewRequestId) {
                stopStreamTracks(stream);
                return null;
            }
            this._videoPreviewStream = stream;
            this._videoPreviewSourceType = previewSourceType;
            this._videoPreviewPendingSourceType = null;
            this._videoPreviewPromise = null;
            this._videoPreviewStopPromise = RSVP.Promise.resolve();
            return stream;
        }).bind(this)).catch((function(error) {
            if (previewRequestId === this._videoPreviewRequestId) {
                this._videoPreviewPromise = null;
                this._videoPreviewPendingSourceType = null;
            }
            if (!isStreamActive(this._videoPreviewStream)) {
                this._videoPreviewSourceType = null;
            }
            this._videoPreviewStopPromise = RSVP.Promise.resolve();
            throw error;
        }).bind(this));

        return this._videoPreviewPromise;
    },

    getScreenPreviewStream: function(forceRefresh) {
        return this.getVideoPreviewStream(forceRefresh, {
            sourceType: "screen"
        });
    },

    getCurrentVideoPreviewStream: function() {
        return isStreamActive(this._videoPreviewStream) ? this._videoPreviewStream : null;
    },

    getCurrentVideoPreviewSourceType: function() {
        return this._videoPreviewSourceType || null;
    },

    stopVideoPreview: function() {
        const pendingPreviewPromise = (this._videoPreviewPromise ? this._videoPreviewPromise : RSVP.Promise.resolve()).catch(function() {
            return null;
        });
        this._videoPreviewRequestId = this._videoPreviewRequestId + 1;
        if (this._videoPreviewStream) {
            stopStreamTracks(this._videoPreviewStream);
            this._videoPreviewStream = null;
        }
        this._videoPreviewPromise = null;
        this._videoPreviewSourceType = null;
        this._videoPreviewPendingSourceType = null;
        this._videoPreviewStopPromise = pendingPreviewPromise.then(function() {
            return null;
        });
        return this._videoPreviewStopPromise;
    },

    getScreenRecordingStream: function(options) {
        const streamOptions = options || {};
        const reusableDisplayStream = streamOptions.displayStream && isStreamActive(streamOptions.displayStream) ? streamOptions.displayStream : null;
        const shouldReuseExistingDisplayStream = !!reusableDisplayStream;
        const displayStreamPromise = shouldReuseExistingDisplayStream ? RSVP.Promise.resolve(reusableDisplayStream) : this._getBrowserScreenPreviewStream();

        return displayStreamPromise.then((function(displayStream) {
            return this._getBrowserMediaStream("audio", streamOptions).then(function(audioStream) {
                const tracks = [];
                if (displayStream && displayStream.getVideoTracks) {
                    displayStream.getVideoTracks().forEach(function(track) {
                        if (isTrackLive(track)) {
                            tracks.push(track);
                        }
                    });
                }
                if (audioStream && audioStream.getAudioTracks) {
                    audioStream.getAudioTracks().forEach(function(track) {
                        if (isTrackLive(track)) {
                            tracks.push(track);
                        }
                    });
                }

                if (!tracks.length) {
                    if (!shouldReuseExistingDisplayStream) {
                        stopStreamTracks(displayStream);
                    }
                    stopStreamTracks(audioStream);
                    throw new Error("No live screen recording tracks were made available.");
                }

                return new MediaStream(tracks);
            }).catch(function(error) {
                if (!shouldReuseExistingDisplayStream) {
                    stopStreamTracks(displayStream);
                }
                throw error;
            });
        }).bind(this));
    },

    getBrowserVideoRecordingStreamForSourceType: function(sourceType, options) {
        const normalizedSourceType = sourceType === "screen" ? "screen" : "camera";
        if (normalizedSourceType === "screen") {
            return this.getScreenRecordingStream(options);
        }

        return this._getBrowserMediaStream("video", options);
    },

    switchBrowserRecordingVideoSource: function(fileName, sourceType) {
        const normalizedSourceType = sourceType === "screen" ? "screen" : "camera";
        if (!(window.device && window.device.platform == "browser" && !(window.trebble.useNativeRecording && "Media" in window))) {
            return RSVP.Promise.reject(new Error(window.getI18n(ti18n, "RECORDING_IS_NOT_SUPPORTED")));
        }

        const recorderObj = this._recordMediaObjects[fileName];
        const recordingStream = recorderObj && recorderObj.getStream ? recorderObj.getStream() : null;
        if (!recordingStream || !recordingStream.getVideoTracks) {
            return RSVP.Promise.reject(new Error("No active browser video recording stream was found."));
        }

        const sourceStreamPromise = normalizedSourceType === "screen" ? this._getPreviewStreamForSourceType(normalizedSourceType) : this._getBrowserCameraRecordingSourceStream();

        return sourceStreamPromise.then((function(sourceStream) {
            const nextVideoTrack = sourceStream && sourceStream.getVideoTracks ? sourceStream.getVideoTracks().find(isTrackLive) : null;
            if (!nextVideoTrack) {
                stopStreamTracks(sourceStream);
                throw new Error("No live video track was made available for the requested source.");
            }

            const applyLegacyTrackSwap = (function() {
                const previousVideoTracks = recordingStream.getVideoTracks().slice();
                if (recorderObj && recorderObj.markVideoSourceSwitched) {
                    recorderObj.markVideoSourceSwitched();
                }
                previousVideoTracks.forEach(function(track) {
                    try {
                        recordingStream.removeTrack(track);
                    } catch (error) {
                        console.warn("Unable to detach the previous video track from the active recording stream", error);
                    }
                });

                try {
                    recordingStream.addTrack(nextVideoTrack);
                } catch (error) {
                    stopStreamTracks(sourceStream);
                    throw error;
                }

                previousVideoTracks.forEach(function(track) {
                    try {
                        track.stop();
                    } catch (error) {
                        console.warn("Unable to stop the previous active recording video track", error);
                    }
                });

                if (sourceStream && sourceStream.getTracks) {
                    sourceStream.getTracks().forEach(function(track) {
                        if (track !== nextVideoTrack && isTrackLive(track)) {
                            try {
                                track.stop();
                            } catch (error) {
                                console.warn("Unable to stop an unused source track after switching the active recording video source", error);
                            }
                        }
                    });
                }

                this._videoPreviewStream = sourceStream;
                this._videoPreviewSourceType = normalizedSourceType;
                return sourceStream;
            }).bind(this);

            if (recorderObj && recorderObj.replaceVideoSourceStream) {
                return RSVP.Promise.resolve(recorderObj.replaceVideoSourceStream(sourceStream)).then((function(updatedRecordingStream) {
                    const previewStream = (recorderObj.getPreviewStream ? recorderObj.getPreviewStream() : null) || sourceStream || updatedRecordingStream || recordingStream;
                    this._videoPreviewStream = previewStream;
                    this._videoPreviewSourceType = normalizedSourceType;
                    return previewStream;
                }).bind(this)).catch((function(error) {
                    const errorMessage = ((error && error.message) || (error && error.name) || String(error) || "").toLowerCase();
                    if (errorMessage.indexOf("does not support live video source switching") >= 0) {
                        return applyLegacyTrackSwap();
                    }

                    stopStreamTracks(sourceStream);
                    throw error;
                }).bind(this));
            }

            return applyLegacyTrackSwap();
        }).bind(this));
    },

    _shouldCreateAudioContextForMediaType: function(recordingMediaType) {
        return recordingMediaType !== "video" && !shouldUseMediaRecorderForAudioRecording();
    },

    _buildBrowserRecorder: function(fileName, recordingMediaType, stream, recordingOptions) {
        const mediaType = recordingMediaType === "video" ? "video" : "audio";
        const shouldCreateAudioContext = this._shouldCreateAudioContextForMediaType(mediaType);
        const supportedMimeType = TrebbleRecorder.getSupportedRecorderMimeInfo(mediaType);
        const browserRecordingOptions = recordingOptions || {};

        if (mediaType === "video" && !supportedMimeType) {
            throw new Error(window.getI18n(ti18n, "RECORDING_IS_NOT_SUPPORTED"));
        }

        if (shouldCreateAudioContext && !window._trebbleAudioContext) {
            this._createNewAudioContext();
        }

        return new TrebbleRecorder({
            useMediaRecorderForAudioRecording: shouldUseMediaRecorderForAudioRecording(),
            stream: stream,
            processor: shouldCreateAudioContext && window._trebbleAudioContext ? window._trebbleAudioContext._processor : null,
            nodeAlreadyConnected: shouldCreateAudioContext && window._trebbleAudioContext ? window._trebbleAudioContext.nodeAlreadyConnected : false,
            mediaType: mediaType,
            fileName: fileName,
            originRoute: browserRecordingOptions.originRoute || null,
            originFlow: browserRecordingOptions.originFlow || null,
            userId: browserRecordingOptions.userId || null,
            useVideoRecordingComposer: browserRecordingOptions.useVideoRecordingComposer === true,
            useAudioTrackStabilizer: browserRecordingOptions.useAudioTrackStabilizer,
            enableDurableDrafts: this.isDurableBrowserRecordingDraftsEnabled()
        });
    },

    _releaseBrowserRecorderAfterStartFailure: function(recorderObj) {
        try {
            if (recorderObj && recorderObj.release) {
                recorderObj.release();
            } else if (recorderObj && recorderObj.clear) {
                recorderObj.clear();
            }
        } catch (error) {
            console.warn("Unable to release the browser recorder after start failed", error);
        }
    },

    getBrowserRecorder: function(fileName, recordingMediaType) {
        const mediaType = recordingMediaType === "video" ? "video" : this.getRecordingMediaType();
        const existingStream = arguments.length > 2 ? arguments[2] : null;
        const recordingOptions = arguments.length > 3 ? arguments[3] : null;
        if (mediaType === "video" && !TrebbleRecorder.getSupportedRecorderMimeInfo("video")) {
            return RSVP.Promise.reject(window.getI18n(ti18n, "RECORDING_IS_NOT_SUPPORTED"));
        }
        try {
            window.URL = window.URL || window.webkitURL;
            if (this._shouldCreateAudioContextForMediaType(mediaType) && !window._trebbleAudioContext) {
                this._createNewAudioContext();
            }
        } catch (e) {
            if (e && e.message) {
                console.error("Cannot get user media. Error:" + e.message);

            }
            return RSVP.Promise.reject(e);
        }

        return new RSVP.Promise((function(resolve, reject) {

            try {

                const startUserMedia = (function(stream) {
                    try {
                        const recorder = this._buildBrowserRecorder(fileName, mediaType, stream, recordingOptions);
                        resolve(recorder);
                    } catch (e) {
                        if (!existingStream) {
                            stopStreamTracks(stream);
                        }
                        reject(e);
                    }
                }).bind(this);

                const browserStreamPromise = existingStream ? RSVP.Promise.resolve(existingStream) : this._getBrowserMediaStream(mediaType);
                browserStreamPromise.then(startUserMedia).catch(function(e) {
                    if (e && e.message) {
                        console.error("Cannot get user media. Error:" + e.message);
                    }
                    reject(e);

                });
    } catch (e) {
        if (e && e.message) {
            console.error("Cannot get user media. Error:" + e.message);

        }
        reject(e);
    }
}).bind(this));
    },

    _getRecorderProcessorDefaultConfig: function() {
        return {
            bufferLen: 4096,
            numChannels: 2
        };
    },

    getRecordingAlternativeMessageInstruction: function() {
        const recordingMediaType = arguments.length > 0 && arguments[0] === "video" ? "video" : this.getRecordingMediaType();
        let warningMessage = window.getI18n(ti18n, recordingMediaType === "video" ? "RECORD_VIDEO_USING_FAVORITE_SOFTWARE_THEN_UPLOAD_FROM_BROWSER" : "RECORD_USING_FAVORITE_SOFTWARE_THEN_UPLOAD_FROM_BROWSER");
        if (window.isMobileBrowser) {
            if (window.isSafariBrowser && recordingMediaType !== "video") {
                warningMessage = window.getI18n(ti18n, "RECORD_USING_VOICE_MEMO_APP_THEN_UPLOAD");
            } else if (recordingMediaType === "video") {
                warningMessage = window.getI18n(ti18n, "RECORD_VIDEO_USING_FAVORITE_SOFTWARE_THEN_UPLOAD_FROM_DEVICE");
            } else {
                warningMessage = window.getI18n(ti18n, "RECORD_USING_FAVORITE_SOFTWARE_THEN_UPLOAD_FROM_DEVICE");
            }
        }
        return warningMessage;
    },

    getBrowserRecorderAndStartRecord: function(fileName, recordingMediaType) {
        const mediaType = recordingMediaType === "video" ? "video" : this.getRecordingMediaType();
        const existingStream = arguments.length > 2 ? arguments[2] : null;
        const recordingOptions = arguments.length > 3 ? arguments[3] : null;
        if (mediaType === "video" && !TrebbleRecorder.getSupportedRecorderMimeInfo("video")) {
            return RSVP.Promise.reject(window.getI18n(ti18n, "RECORDING_IS_NOT_SUPPORTED"));
        }

        try {
            window.URL = window.URL || window.webkitURL;
            if (this._shouldCreateAudioContextForMediaType(mediaType) && !window._trebbleAudioContext) {
                this._createNewAudioContext();
            }
        } catch (e) {
            if (e && e.message) {
                console.error("Cannot get user media. Error:" + e.message);

            }
            return RSVP.Promise.reject(e);
        }

        return new RSVP.Promise((function(resolve, reject) {

            try {

                const startUserMedia = (function(stream) {
                    try {
                        const recorder = this._buildBrowserRecorder(fileName, mediaType, stream, recordingOptions);
                        RSVP.Promise.resolve().then(function() {
                            return recorder.record();
                        }).then((function() {
                            this._recordMediaObjects[fileName] = recorder;
                            resolve(recorder);
                        }).bind(this)).catch((function(error) {
                            this._releaseBrowserRecorderAfterStartFailure(recorder);
                            reject(error);
                        }).bind(this));
                    } catch (e) {
                        if (!existingStream) {
                            stopStreamTracks(stream);
                        }
                        reject(e);
                    }
                }).bind(this);

                const browserStreamPromise = existingStream ? RSVP.Promise.resolve(existingStream) : this._getBrowserMediaStream(mediaType);
                browserStreamPromise.then(startUserMedia).catch(function(e) {
                    if (e && e.message) {
                        console.error("Cannot get user media. Error:" + e.message);
                    }
                    reject(e);

                });
            } catch (e) {
                if (e && e.message) {
                    console.error("Cannot get user media. Error:" + e.message);

                }
                reject(e);
            }
        }).bind(this));
    },

applyGetUserMediaPatchForOldBrowsers: function() {
// Older browsers might not implement mediaDevices at all, so we set an empty object first
if (navigator.mediaDevices === undefined) {
navigator.mediaDevices = {};
}

// Some browsers partially implement mediaDevices. We can't just assign an object
// with getUserMedia as it would overwrite existing properties.
// Here, we will just add the getUserMedia property if it's missing.
if (navigator.mediaDevices.getUserMedia === undefined) {
navigator.mediaDevices.getUserMedia = function(constraints) {

    // First get ahold of the legacy getUserMedia, if present
    const getUserMedia = navigator.webkitGetUserMedia || navigator.mozGetUserMedia;

    // Some browsers just don't implement it - return a rejected promise with an error
    // to keep a consistent interface
    if (!getUserMedia) {
        return Promise.reject(new Error(window.getI18n(ti18n, "RECORDING_IS_NOT_SUPPORTED")));
    }

    // Otherwise, wrap the call to the old navigator.getUserMedia with a Promise
    return new Promise(function(resolve, reject) {
        getUserMedia.call(navigator, constraints, resolve, reject);
    });
}
}

},

initUserMedia: function() {
// FOR NOW THIS JSUT MAKE SURE AUDIOCONTEXT IS SUPPORTED
return RSVP.Promise.resolve(!!(window.AudioContext || window.webkitAudioContext));

},

isRecording: function() {
return this._isRecording;
},

_buildRecordedMediaValidationError: function(fileName, diagnostics) {
    const validationErrorMessage = window.getI18n(ti18n, "AN_ERROR_OCCURED_WHILE_RENDERING_YOUR_AUDIO_RECORDING");
    console.error("Recorded media validation failed", Object.assign({
        fileName: fileName
    }, diagnostics || {}));
    return new Error(validationErrorMessage);
},

_getRecordingHealthInfo: function(recorderObj) {
    if (recorderObj && recorderObj.getRecordingHealth) {
        return recorderObj.getRecordingHealth();
    }

    return null;
},

_getDurationFromRecordingHealthInSeconds: function(recordingHealth) {
    if (!recordingHealth || typeof recordingHealth.startedAt !== "number" || typeof recordingHealth.endedAt !== "number") {
        return null;
    }

    const durationInMilli = recordingHealth.endedAt - recordingHealth.startedAt;
    if (!(durationInMilli > 0)) {
        return null;
    }

    return durationInMilli / 1000;
},

_normalizeRecordedMediaMetadata: function(mediaMetadata, recordingHealth) {
    if (!mediaMetadata) {
        return mediaMetadata;
    }

    const normalizedMediaMetadata = Object.assign({}, mediaMetadata);
    const durationFromRecordingHealthInSeconds = this._getDurationFromRecordingHealthInSeconds(recordingHealth);

    if ((!isFinite(normalizedMediaMetadata.duration) || normalizedMediaMetadata.duration <= 0) && durationFromRecordingHealthInSeconds && isFinite(durationFromRecordingHealthInSeconds)) {
        normalizedMediaMetadata.duration = durationFromRecordingHealthInSeconds;
        normalizedMediaMetadata.durationSource = "recordingHealth";
    }

    return normalizedMediaMetadata;
},

_isMediaRecorderBackedRecording: function(recorderObj, recordedFileBlob) {
    const mimeType = (recordedFileBlob && recordedFileBlob.type) || (recorderObj && recorderObj.getMimeType ? recorderObj.getMimeType() : null);
    return !!(mimeType && mimeType !== "audio/wav");
},

_buildRecordedMediaMetadataFallback: function(recordedFileBlob, recorderObj, mediaMetadata) {
    const recordingHealth = this._getRecordingHealthInfo(recorderObj);
    const normalizedMediaMetadata = this._normalizeRecordedMediaMetadata(mediaMetadata || {}, recordingHealth) || {};
    const mimeType = (recordedFileBlob && recordedFileBlob.type) || (recorderObj && recorderObj.getMimeType ? recorderObj.getMimeType() : null);
    const recorderMediaType = recorderObj && recorderObj.getMediaType ? recorderObj.getMediaType() : null;
    const durationFromRecordingHealthInSeconds = this._getDurationFromRecordingHealthInSeconds(recordingHealth);
    const resolvedDuration = (isFinite(normalizedMediaMetadata.duration) && normalizedMediaMetadata.duration > 0) ? normalizedMediaMetadata.duration : ((durationFromRecordingHealthInSeconds && isFinite(durationFromRecordingHealthInSeconds)) ? durationFromRecordingHealthInSeconds : null);

    return Object.assign({}, normalizedMediaMetadata, {
        duration: resolvedDuration,
        isVideo: typeof normalizedMediaMetadata.isVideo === "boolean" ? normalizedMediaMetadata.isVideo : !!(recorderMediaType === "video" || (mimeType && mimeType.indexOf("video/") === 0)),
        width: normalizedMediaMetadata.width > 0 ? normalizedMediaMetadata.width : null,
        height: normalizedMediaMetadata.height > 0 ? normalizedMediaMetadata.height : null
    });
},

_getMinimumReliableRecordedMediaSizeInBytes: function(mediaMetadata) {
    const isVideo = !!(mediaMetadata && mediaMetadata.isVideo);
    const durationInSeconds = mediaMetadata && mediaMetadata.duration ? mediaMetadata.duration : 0;

    if (isVideo) {
        return durationInSeconds >= 1 ? MIN_RELIABLE_VIDEO_RECORDING_SIZE_OVER_ONE_SECOND_IN_BYTES : MIN_RELIABLE_VIDEO_RECORDING_SIZE_IN_BYTES;
    }

    return durationInSeconds >= 1 ? MIN_RELIABLE_AUDIO_RECORDING_SIZE_OVER_ONE_SECOND_IN_BYTES : MIN_RELIABLE_AUDIO_RECORDING_SIZE_IN_BYTES;
},

_isWebmRecordedMedia: function(fileName, recordedFileBlob, recorderObj) {
    const mimeType = ((recordedFileBlob && recordedFileBlob.type) || (recorderObj && recorderObj.getMimeType ? recorderObj.getMimeType() : "") || "").toLowerCase();
    const normalizedFileName = (fileName || "").toLowerCase();
    return mimeType.indexOf("webm") >= 0 || /\.webm($|\?)/.test(normalizedFileName);
},

_arrayHasBytesAt: function(bytes, offset, expectedBytes) {
    if (!bytes || !expectedBytes || offset < 0 || offset + expectedBytes.length > bytes.length) {
        return false;
    }

    for (let index = 0; index < expectedBytes.length; index++) {
        if (bytes[offset + index] !== expectedBytes[index]) {
            return false;
        }
    }
    return true;
},

_getEbmlVintLength: function(firstByte, maxLength) {
    if (!firstByte) {
        return 0;
    }

    let marker = 0x80;
    const normalizedMaxLength = maxLength && maxLength > 0 ? maxLength : EBML_MAX_SIZE_LENGTH_IN_BYTES;
    for (let length = 1; length <= normalizedMaxLength; length++) {
        if (firstByte & marker) {
            return length;
        }
        marker = marker >> 1;
    }
    return 0;
},

_readEbmlElementId: function(bytes, offset, endOffset) {
    const effectiveEndOffset = typeof endOffset === "number" ? Math.min(endOffset, bytes.length) : bytes.length;
    if (!bytes || offset < 0 || offset >= effectiveEndOffset) {
        return null;
    }

    const length = this._getEbmlVintLength(bytes[offset], EBML_MAX_ELEMENT_ID_LENGTH_IN_BYTES);
    if (!length || offset + length > effectiveEndOffset) {
        return null;
    }

    let value = 0;
    for (let index = 0; index < length; index++) {
        value = (value * 256) + bytes[offset + index];
    }
    return {
        length: length,
        value: value,
        nextOffset: offset + length
    };
},

_readEbmlSize: function(bytes, offset, endOffset) {
    const effectiveEndOffset = typeof endOffset === "number" ? Math.min(endOffset, bytes.length) : bytes.length;
    if (!bytes || offset < 0 || offset >= effectiveEndOffset) {
        return null;
    }

    const length = this._getEbmlVintLength(bytes[offset], EBML_MAX_SIZE_LENGTH_IN_BYTES);
    if (!length || offset + length > effectiveEndOffset) {
        return null;
    }

    const firstByteValueMask = 0xff >> length;
    let value = bytes[offset] & firstByteValueMask;
    let isUnknownSize = value === firstByteValueMask;
    for (let index = 1; index < length; index++) {
        value = (value * 256) + bytes[offset + index];
        isUnknownSize = isUnknownSize && bytes[offset + index] === 0xff;
    }

    return {
        length: length,
        value: value,
        nextOffset: offset + length,
        isUnknownSize: isUnknownSize
    };
},

_decodeAsciiBytes: function(bytes, startOffset, endOffset) {
    let value = "";
    for (let index = startOffset; index < endOffset; index++) {
        value = value + String.fromCharCode(bytes[index]);
    }
    return value;
},

_ebmlHeaderContainsSupportedDocType: function(bytes, startOffset, endOffset) {
    let offset = startOffset;
    while (offset < endOffset) {
        const elementId = this._readEbmlElementId(bytes, offset, endOffset);
        if (!elementId) {
            return false;
        }

        const elementSize = this._readEbmlSize(bytes, elementId.nextOffset, endOffset);
        if (!elementSize || elementSize.isUnknownSize) {
            return false;
        }

        const valueStartOffset = elementSize.nextOffset;
        const valueEndOffset = valueStartOffset + elementSize.value;
        if (valueEndOffset > endOffset || valueEndOffset < valueStartOffset) {
            return false;
        }

        if (elementId.value === WEBM_DOCTYPE_ELEMENT_ID) {
            const docType = this._decodeAsciiBytes(bytes, valueStartOffset, valueEndOffset).toLowerCase();
            return SUPPORTED_WEBM_DOCTYPES.indexOf(docType) >= 0;
        }
        offset = valueEndOffset;
    }

    return false;
},

_isValidWebmEbmlHeaderAt: function(bytes, offset) {
    if (!this._arrayHasBytesAt(bytes, offset, WEBM_EBML_HEADER_BYTES)) {
        return false;
    }

    const headerSize = this._readEbmlSize(bytes, offset + WEBM_EBML_HEADER_BYTES.length, bytes.length);
    if (!headerSize || headerSize.isUnknownSize) {
        return false;
    }

    const headerStartOffset = headerSize.nextOffset;
    const headerEndOffset = headerStartOffset + headerSize.value;
    if (headerEndOffset > bytes.length || headerEndOffset < headerStartOffset) {
        return false;
    }

    return this._ebmlHeaderContainsSupportedDocType(bytes, headerStartOffset, headerEndOffset);
},

_countEbmlHeadersInArrayBuffer: function(arrayBuffer) {
    if (!arrayBuffer) {
        return 0;
    }

    const bytes = new Uint8Array(arrayBuffer);
    let headerCount = 0;
    for (let index = 0; index <= bytes.length - WEBM_EBML_HEADER_BYTES.length; index++) {
        if (this._isValidWebmEbmlHeaderAt(bytes, index)) {
            headerCount = headerCount + 1;
            if (headerCount > 1) {
                return headerCount;
            }
        }
    }
    return headerCount;
},

_countEbmlHeadersInBlob: function(recordedFileBlob) {
    if (!recordedFileBlob || typeof recordedFileBlob.slice !== "function" || typeof recordedFileBlob.arrayBuffer !== "function" || !(recordedFileBlob.size > 0)) {
        return RSVP.Promise.resolve(0);
    }

    const blobSize = recordedFileBlob.size;
    const headerOffsets = [];
    let nextChunkStartOffset = 0;

    const scanNextChunk = (function() {
        if (nextChunkStartOffset >= blobSize || headerOffsets.length > 1) {
            return RSVP.Promise.resolve(headerOffsets.length);
        }

        const chunkStartOffset = Math.max(0, nextChunkStartOffset - WEBM_HEADER_SCAN_OVERLAP_IN_BYTES);
        const chunkEndOffset = Math.min(blobSize, nextChunkStartOffset + WEBM_HEADER_SCAN_CHUNK_SIZE_IN_BYTES);
        nextChunkStartOffset = chunkEndOffset;

        return RSVP.Promise.resolve(recordedFileBlob.slice(chunkStartOffset, chunkEndOffset).arrayBuffer()).then((function(arrayBuffer) {
            const bytes = new Uint8Array(arrayBuffer);
            for (let index = 0; index <= bytes.length - WEBM_EBML_HEADER_BYTES.length; index++) {
                if (this._isValidWebmEbmlHeaderAt(bytes, index)) {
                    const absoluteOffset = chunkStartOffset + index;
                    if (headerOffsets.indexOf(absoluteOffset) < 0) {
                        headerOffsets.push(absoluteOffset);
                        if (headerOffsets.length > 1) {
                            return headerOffsets.length;
                        }
                    }
                }
            }

            return new RSVP.Promise(function(resolve) {
                setTimeout(resolve, 0);
            }).then(scanNextChunk);
        }).bind(this));
    }).bind(this);

    return scanNextChunk();
},

_validateRecordedMediaContainerStructure: function(fileName, recordedFileBlob, recorderObj) {
    if (!this._isWebmRecordedMedia(fileName, recordedFileBlob, recorderObj) || !recordedFileBlob || typeof recordedFileBlob.slice !== "function" || typeof recordedFileBlob.arrayBuffer !== "function") {
        return RSVP.Promise.resolve();
    }

    return this._countEbmlHeadersInBlob(recordedFileBlob).then((function(ebmlHeaderCount) {
        if (ebmlHeaderCount > 1) {
            throw this._buildRecordedMediaValidationError(fileName, {
                reason: "multiple_webm_headers",
                ebmlHeaderCount: ebmlHeaderCount,
                mediaBlobSize: recordedFileBlob.size,
                mimeType: recordedFileBlob.type || (recorderObj && recorderObj.getMimeType ? recorderObj.getMimeType() : null),
                recordingHealth: this._getRecordingHealthInfo(recorderObj)
            });
        }
    }).bind(this));
},

_validateRecordedMediaBlob: function(fileName, recordedFileBlob, recorderObj, mediaMetadata) {
    const recordingHealth = this._getRecordingHealthInfo(recorderObj);
    const normalizedMediaMetadata = this._buildRecordedMediaMetadataFallback(recordedFileBlob, recorderObj, mediaMetadata);
    const usesMediaRecorder = this._isMediaRecorderBackedRecording(recorderObj, recordedFileBlob);
    const mediaBlobSize = recordedFileBlob && typeof recordedFileBlob.size === "number" ? recordedFileBlob.size : 0;
    const diagnostics = {
        mediaBlobSize: mediaBlobSize,
        mediaMetadata: normalizedMediaMetadata,
        recordingHealth: recordingHealth,
        recordingHealthDurationInSeconds: this._getDurationFromRecordingHealthInSeconds(recordingHealth),
        mimeType: (recordedFileBlob && recordedFileBlob.type) || (recorderObj && recorderObj.getMimeType ? recorderObj.getMimeType() : null)
    };

    if (!recordedFileBlob || !(mediaBlobSize > 0)) {
        throw this._buildRecordedMediaValidationError(fileName, diagnostics);
    }

    const resolvedDurationInSeconds = normalizedMediaMetadata && isFinite(normalizedMediaMetadata.duration) && normalizedMediaMetadata.duration > 0 ? normalizedMediaMetadata.duration : null;
    if (resolvedDurationInSeconds === null) {
        // Blocking upload here instead of letting null propagate downstream — a clip
        // without a duration is always rejected by the transcription pipeline and the
        // server-side error ("please try re-uploading") is misleading because the
        // re-upload would hit the exact same extraction failure.
        throw this._buildRecordedMediaValidationError(fileName, diagnostics);
    }

    if (mediaBlobSize < this._getMinimumReliableRecordedMediaSizeInBytes(normalizedMediaMetadata)) {
        console.warn("Recorded media is smaller than the reliability heuristic but will be accepted", Object.assign({
            fileName: fileName
        }, diagnostics));
    }

    if (usesMediaRecorder && recordingHealth) {
        if (!(recordingHealth.chunksStored > 0) || !(recordingHealth.bytesStored > 0)) {
            console.warn("Recorded media health counters look incomplete but the non-empty blob will be accepted", Object.assign({
                fileName: fileName
            }, diagnostics));
        }
    }

    return normalizedMediaMetadata;
},

_cleanupBrowserRecordingState: function(fileName, recorderObj) {
    const cleanupPromises = [];
    const mediaInfo = this._recordFilenameToMediaInfo[fileName];
    if (mediaInfo && mediaInfo.localUrl) {
        try {
            URL.revokeObjectURL(mediaInfo.localUrl);
        } catch (_) {
            // Ignore revoke failures while cleaning partial recordings.
        }
    }

    if (recorderObj) {
        if (this.canAccessDurableBrowserRecordingDrafts()) {
            if (recorderObj.release) {
                recorderObj.release();
            } else if (recorderObj.clear) {
                recorderObj.clear();
            }
            if (recorderObj.getRecordingId) {
                const recordingId = recorderObj.getRecordingId();
                if (recordingId) {
                    cleanupPromises.push(
                        RSVP.Promise.resolve(BrowserRecordingDraftStore.getInstance().markRecoverableFailure(recordingId, new Error("The browser recording was interrupted before the local draft finished hydrating."), {
                            fileName: fileName
                        })).catch(function(error) {
                            console.warn("Unable to mark the interrupted browser recording as recoverable", error);
                            return null;
                        })
                    );
                } else if (recorderObj.deletePersistedDraft) {
                    cleanupPromises.push(
                        RSVP.Promise.resolve(recorderObj.deletePersistedDraft()).catch(function(error) {
                            console.warn("Failed to delete the partial persisted browser recording state", error);
                            return null;
                        })
                    );
                }
            } else if (recorderObj.deletePersistedDraft) {
                cleanupPromises.push(
                    RSVP.Promise.resolve(recorderObj.deletePersistedDraft()).catch(function(error) {
                        console.warn("Failed to delete the partial persisted browser recording state", error);
                        return null;
                    })
                );
            }
        } else if (recorderObj.deletePersistedDraft) {
            cleanupPromises.push(
                RSVP.Promise.resolve(recorderObj.deletePersistedDraft()).catch(function(error) {
                    console.warn("Failed to delete the partial persisted browser recording state", error);
                    return null;
                })
            );
        } else if (recorderObj.clear) {
            recorderObj.clear();
        }
    }

    return RSVP.Promise.all(cleanupPromises).then((function() {
        delete this._recordMediaObjects[fileName];
        delete this._recordMediaFilenames[fileName];
        delete this._recordFilenameToBlob[fileName];
        delete this._recordFilenameToMediaInfo[fileName];
        delete this._recordFilenameToDurableDraftManifest[fileName];
        return null;
    }).bind(this));
},

_resolveRecordedMediaMetadata: function(fileName, recordedFileBlob, recorderObj) {
    return this._withTimeout(
        Utils.getInstance().getMediaMetadata(recordedFileBlob, true),
        RECORDED_MEDIA_METADATA_TIMEOUT_IN_MILLI,
        "Timed out while extracting recorded media metadata."
    ).catch((function(error) {
        console.warn("Unable to extract recorded media metadata, falling back to blob-level info", {
            fileName: fileName,
            mediaBlobSize: recordedFileBlob && recordedFileBlob.size ? recordedFileBlob.size : 0,
            mediaMetadataError: error,
            recordingHealth: this._getRecordingHealthInfo(recorderObj)
        });
        return null;
    }).bind(this));
},

_resolveRecordedMediaLocalUrl: function(fileName, recordedFileBlob) {
    const url = URL.createObjectURL(recordedFileBlob);
    if (!window.isMobileBrowser) {
        return RSVP.Promise.resolve(url);
    }

    return this._withTimeout(
        this.getNewBlobXHRUrl(url, recordedFileBlob.type),
        RECORDED_MEDIA_LOCAL_URL_TIMEOUT_IN_MILLI,
        "Timed out while preparing the recorded media for playback.",
        function(xhrBlobUrl) {
            if (xhrBlobUrl) {
                try {
                    URL.revokeObjectURL(xhrBlobUrl);
                } catch (_) {
                    // Ignore URL cleanup failures for late results.
                }
            }
        }
    ).then(function(xhrBlobUrl) {
        try {
            URL.revokeObjectURL(url);
        } catch (_) {
            // Ignore URL cleanup failures.
        }
        return xhrBlobUrl;
    }).catch(function(error) {
        console.warn("Falling back to the original blob URL for the recorded media", {
            fileName: fileName,
            mediaBlobSize: recordedFileBlob && recordedFileBlob.size ? recordedFileBlob.size : 0,
            error: error
        });
        return url;
    });
},

_buildRecordedMediaInfo: function(fileName, recordedFileBlob, recorderObj) {
    // Audio recordings already carry enough timing information from the
    // recorder health counters, so waiting on browser metadata parsing here
    // only delays the UI after stop. Keep the richer metadata fetch for video,
    // where width/height/thumbnail still matter for preview and upload flows.
    const mediaMetadataFallback = this._buildRecordedMediaMetadataFallback(recordedFileBlob, recorderObj, null);
    const shouldResolveFullMediaMetadata = !!(mediaMetadataFallback && mediaMetadataFallback.isVideo);
    const mediaMetadataPromise = shouldResolveFullMediaMetadata ? this._resolveRecordedMediaMetadata(fileName, recordedFileBlob, recorderObj) : RSVP.Promise.resolve(mediaMetadataFallback);

    return mediaMetadataPromise.then((function(mediaMetadata) {
        const validatedMediaMetadata = this._validateRecordedMediaBlob(fileName, recordedFileBlob, recorderObj, mediaMetadata);
        const resolvedDurationInMilli = validatedMediaMetadata && isFinite(validatedMediaMetadata.duration) && validatedMediaMetadata.duration > 0 ? validatedMediaMetadata.duration * 1000 : 0;
        const recordingHealth = this._getRecordingHealthInfo(recorderObj);

        return this._validateRecordedMediaContainerStructure(fileName, recordedFileBlob, recorderObj).then((function() {
            return this._resolveRecordedMediaLocalUrl(fileName, recordedFileBlob).then((function(localUrl) {
                const recordedFile = Utils.getInstance().createFileWithNewName(recordedFileBlob, fileName);
                if (recordingHealth) {
                    recordedFile._recordingHealth = recordingHealth;
                }
                const mediaInfo = {
                    fileName: fileName,
                    originalFilename: fileName,
                    blob: recordedFileBlob,
                    file: recordedFile,
                    localUrl: localUrl,
                    mimeType: recordedFileBlob.type || (recorderObj && recorderObj.getMimeType ? recorderObj.getMimeType() : null),
                    size: recordedFileBlob.size,
                    duration: resolvedDurationInMilli,
                    isVideo: !!validatedMediaMetadata.isVideo,
                    recordingHealth: recordingHealth
                };

                if (mediaInfo.isVideo) {
                    if (validatedMediaMetadata.width) {
                        mediaInfo.width = validatedMediaMetadata.width;
                    }
                    if (validatedMediaMetadata.height) {
                        mediaInfo.height = validatedMediaMetadata.height;
                    }
                    if (validatedMediaMetadata.thumbnail) {
                        mediaInfo.thumbnail = validatedMediaMetadata.thumbnail;
                    }
                    if (validatedMediaMetadata.thumbnailWidth) {
                        mediaInfo.thumbnailWidth = validatedMediaMetadata.thumbnailWidth;
                    }
                    if (validatedMediaMetadata.thumbnailHeight) {
                        mediaInfo.thumbnailHeight = validatedMediaMetadata.thumbnailHeight;
                    }
                }

                this._recordFilenameToBlob[fileName] = recordedFileBlob;
                this._recordFilenameToMediaInfo[fileName] = mediaInfo;
                this._recordMediaFilenames[fileName] = mediaInfo.localUrl;
                return mediaInfo;
            }).bind(this));
        }).bind(this));
    }).bind(this));
},

stopRecording: function(fileName) {
this._isRecording = false;
if (window.device && window.device.platform == "browser" && !(window.trebble.useNativeRecording && "Media" in window)) {
    let recorderObj = null;
    this._recordingPromise = (this._recordingPromise ? this._recordingPromise : RSVP.Promise.resolve()).then((function() {
        recorderObj = this._recordMediaObjects[fileName];
        if (recorderObj) {
            return recorderObj.stop();
        }
        return null;
    }).bind(this)).then((function(recordedFileBlob) {
        if (this.isAudioContextCreated()) {
            this._closeAudioContext();
        }
        if (!recordedFileBlob && recorderObj && recorderObj.exportWAV) {
            return new RSVP.Promise((function(resolve, reject) {
                recorderObj.exportWAV(resolve, reject);
            }).bind(this)).then((function(exportedBlob) {
                return exportedBlob;
            }).bind(this));
        }
        return recordedFileBlob;
    }).bind(this)).then((function(recordedFileBlob) {
        return this.stopPlayRecordingsIfApplicable().then((function() {
            if (!recordedFileBlob) {
                return null;
            }
            if (recordedFileBlob && recordedFileBlob.size !== 0 && recordedFileBlob.size > 0) {
                try {
                    if (window.trebbleAnalyticsHelper) {
                        window.trebbleAnalyticsHelper.trackEvent("Capsule", "capsuleFinishedBrowserRecording", "Capsule Finish Browser Recording", recordedFileBlob.size);
                    }
                } catch (error) {
                    console.error("There was an error logging capsuleFinishedRecording Infor. Error:" + error);
                }
                return this._buildRecordedMediaInfo(fileName, recordedFileBlob, recorderObj).then((function(mediaInfo) {
                    if (!this.canAccessDurableBrowserRecordingDrafts() || !recorderObj || !recorderObj.getRecordingId) {
                        return mediaInfo;
                    }

                    return RSVP.Promise.resolve(BrowserRecordingDraftStore.getInstance().getDraft(recorderObj.getRecordingId())).then((function(draftManifest) {
                        if (draftManifest) {
                            this._registerDurableDraftManifest(fileName, draftManifest);
                        }
                        return mediaInfo;
                    }).bind(this)).catch(function(error) {
                        console.warn("Unable to refresh the durable browser recording draft manifest after stopping the recorder", error);
                        return mediaInfo;
                    });
                }).bind(this));
            }
            try {
                if (window.trebbleAnalyticsHelper) {
                    window.trebbleAnalyticsHelper.trackEvent("Capsule", "errorOccuredDuringCapsuleRendering", "Error During Capsule Rendering", recordedFileBlob && recordedFileBlob.size);
                }
            } catch (error) {
                console.error("There was an error logging than error occured during the capsule rendering. Error:" + error);
            }
            throw window.getI18n(ti18n, "AN_ERROR_OCCURED_WHILE_RENDERING_YOUR_AUDIO_RECORDING");
        }).bind(this));
    }).bind(this)).catch((function(error) {
        if (this.isAudioContextCreated()) {
            this._closeAudioContext();
        }
        return this._cleanupBrowserRecordingState(fileName, recorderObj).then(function() {
            throw error;
        });
    }).bind(this));
    return this._recordingPromise;

} else {
this._recordingPromise = this._recordingPromise.then((function() {
    const mediaRec = this._recordMediaObjects[fileName];
    if (mediaRec) {
        mediaRec.stopRecord();
    }
}).bind(this));
return this._recordingPromise;
}

},

getNewBlobXHRUrl: function(blobUrl, mimeType) {
return new RSVP.Promise(function(resolve, reject) {
let xhrBlobUrl;

const xhr = new XMLHttpRequest();
xhr.open('GET', blobUrl, true);
xhr.responseType = 'blob';
if (mimeType && xhr.overrideMimeType) {
    xhr.overrideMimeType(mimeType);
}

xhr.onreadystatechange = function() {
    window.log(xhr);
    if (xhr.readyState === 4 && xhr.status == 200) {
        xhrBlobUrl = window.URL.createObjectURL(xhr.response);
        resolve(xhrBlobUrl);
    } else {
        if (xhr.status >= 500) {
            reject(window.getI18n(ti18n, "SOMETHING_WENT_WRONG_WHILE_PROCESSING_YOUR_RECORDING"));
        }
    }
};
xhr.onerror = reject;
xhr.send();
});
},

    deleteRecording: function (fileName)
    {
        const revokeLocalUrlIfApplicable = (function () {
            const mediaInfo = this._recordFilenameToMediaInfo[fileName];
            if (mediaInfo && mediaInfo.localUrl) {
                try {
                    URL.revokeObjectURL(mediaInfo.localUrl);
                } catch (_) {
                    // Ignore revoke failures.
                }
            }
        }).bind(this);
        const waitForRecordingPromise = (this._recordingPromise ? this._recordingPromise : RSVP.Promise.resolve()).catch(function(){
            return null;
        });
        const deletePersistedDraftIfApplicable = (function(recordingId, recorderObj) {
            if (recorderObj && recorderObj.deletePersistedDraft) {
                return RSVP.Promise.resolve(recorderObj.deletePersistedDraft()).then((function() {
                    if (this.canAccessDurableBrowserRecordingDrafts() && recordingId) {
                        return BrowserRecordingDraftStore.getInstance().markDiscarded(recordingId);
                    }
                }).bind(this));
            }

            if (recordingId) {
                return this.deletePersistedRecordingDraft(recordingId);
            }

            return RSVP.Promise.resolve();
        }).bind(this);
        const clearRuntimeState = (function() {
            delete this._recordMediaObjects[fileName];
            delete this._recordMediaFilenames[fileName];
            delete this._recordFilenameToBlob[fileName];
            delete this._recordFilenameToMediaInfo[fileName];
            delete this._recordFilenameToDurableDraftManifest[fileName];
        }).bind(this);

        if (window.device && window.device.platform == "browser" && !(window.trebble.useNativeRecording && "Media" in window))
        {
            return waitForRecordingPromise.then((function ()
            {
                const recorderObj = this._recordMediaObjects[fileName];
                const durableDraftManifest = this.getDurableDraftManifestForRecording(fileName);
                const recordingId = durableDraftManifest && durableDraftManifest.recordingId ? durableDraftManifest.recordingId : (recorderObj && recorderObj.getRecordingId ? recorderObj.getRecordingId() : fileName);

                return deletePersistedDraftIfApplicable(recordingId, recorderObj).catch(function(error) {
                    console.warn("Unable to delete the persisted browser recording draft", error);
                    return null;
                }).then((function() {
                    if (recorderObj && recorderObj.release) {
                        recorderObj.release();
                    } else if (recorderObj && recorderObj.clear) {
                        recorderObj.clear();
                    }
                    revokeLocalUrlIfApplicable();
                    clearRuntimeState();
                }).bind(this));

            }).bind(this));
        } else
        {
            waitForRecordingPromise.then((function ()
            {
                const recorderObj = this._recordMediaObjects[ fileName ];
                if (recorderObj)
                {
                    recorderObj.release();
                }
                revokeLocalUrlIfApplicable();
                clearRuntimeState();

            }).bind(this));
            return FileUploadHelper.getInstance().deleteFileFromPersistentFileSystemUrl(fileName);
        }
    },


playRecordings: function(arrayOfRecordingNames, backgroundTrackUrl, backgroundTrackLoadingHandler, playRecordingRequestId) {
if (window.device && window.device.platform == "browser" && !(window.trebble.useNativeRecording && "Media" in window)) {
return this.stopPlayRecordingsIfApplicable().then((function() {
    this._recordingFilenameInPlayQueue = [];
    for (const index in arrayOfRecordingNames) {
        const recordingName = arrayOfRecordingNames[index];
        const recordingFilename = this._recordMediaFilenames[recordingName];
        if (recordingFilename) {
            this._recordingFilenameInPlayQueue.push(recordingFilename);
        }
    }
    return this._playQueue(backgroundTrackUrl, backgroundTrackLoadingHandler, playRecordingRequestId);
}).bind(this));
} else {
return this.stopPlayRecordingsIfApplicable().then((function() {
    this._recordingFilenameInPlayQueue = [];
    for (const index in arrayOfRecordingNames) {
        const recordingName = arrayOfRecordingNames[index];
        const recordingFilename = this._recordMediaFilenames[recordingName];
        const media = this._recordMediaObjects[recordingName];
        if (media) {
            this._recordingFilenameInPlayQueue.push(recordingFilename);
        }
    }
    return this._playQueue(backgroundTrackUrl, backgroundTrackLoadingHandler, playRecordingRequestId);
}).bind(this));
}

},

_stopAndDestroyBackgroundTrack: function() {
if (this._backgroundTrackMedia && this._backgroundTrackMedia._completelyLoaded) {
this._backgroundTrackMedia.stop();
this._backgroundTrackMedia.release();
}
this._backgroundTrackMedia = null;
this._backgroundTrackUrl = null;
this._playRecordingRequestId = null;
this._backgroundTrackLoadingPromise = null;

},

_downloadBackgroundTrackLocallyIfApplicable: function(backgroundTrackUrl){
return (this.useCordovaMediaObjectForBackgroundMusic() && (USE_NATIVE_AUDIO_FOR_BACKGROUND && window.isIOSMobileDevice) && backgroundTrackUrl)? FileUploadHelper.getInstance().downloadFileLocallyTemporary(backgroundTrackUrl): RSVP.Promise.resolve(backgroundTrackUrl);
},

_loadBackgroundTrackIfApplicable: function(backgroundTrackUrl, requestId) {
window.log("About to start loading background track for url " + backgroundTrackUrl);
if (!backgroundTrackUrl || backgroundTrackUrl !== this._backgroundTrackUrl) {
this._stopAndDestroyBackgroundTrack();

}
this._backgroundTrackUrl = backgroundTrackUrl;
if (!this._backgroundTrackUrl || (this._backgroundTrackMedia && this._backgroundTrackMedia._completelyLoaded)) {
window.log("About to return from loading background track for url " + backgroundTrackUrl);
if (this._backgroundTrackMedia) {
    window.log("Looks like the background audio is already loaded for url " + backgroundTrackUrl);
    const startVolume = this.useCordovaMediaObjectForBackgroundMusic() ? 1 : 100;
    this._backgroundTrackMedia.setVolume(startVolume);
    this._backgroundTrackMedia.seekTo(0);
}
return RSVP.Promise.resolve();
} else {
if (this._backgroundTrackMedia && this._backgroundTrackLoadingPromise) {
    window.log("Looks like the  background track for url " + backgroundTrackUrl + " is loading");
    return this._backgroundTrackLoadingPromise;
}
const p = new RSVP.Promise((function(resolve, reject) {
    let backgroundTrackMedia = null;
    const onBackgroundTrackMediaSuccessPlay = (function() {
        window.log("Successfully finish playing background track ");
    }).bind(this);
    const onBackgroundTrackMediaFailedPlaying = (function(err) {
        window.log("onBackgroundTrackMediaFailedPlaying called for" + backgroundTrackUrl);
        if (this._backgroundTrackLoadingPromise === p) {
            window.log("Error occured playing background track. Error:" + err);
            reject(err);
        }
    }).bind(this);
    const onBackgroundTrackMediaStatusChanged = (function(mediaStatus) {
        window.log("onBackgroundTrackMediaStatusChanged called for" + backgroundTrackUrl);
        if (this._backgroundTrackLoadingPromise === p) {
            window.log("Media status for background track is " + mediaStatus);
            window.log("Media status changed for " + backgroundTrackUrl + " and it is the current background audio");
        }
    }).bind(this);
    const onMediaReadyToPlay = (function() {
        window.log("onMediaReadyToPlay called for" + backgroundTrackUrl);
        if (this.useCordovaMediaObjectForBackgroundMusic() || this._backgroundTrackLoadingPromise === p) {
            //background promise still active
            window.log("background track is ready to play");
            window.log("background track is ready to play changed for " + backgroundTrackUrl);
            resolve();
        }
    }).bind(this);
    if (window.device && window.device.platform == "browser" && !(window.trebble.useNativeRecording && "Media" in window)) {
        backgroundTrackMedia = this._createSoundManagerMedia(this._backgroundTrackUrl, onBackgroundTrackMediaSuccessPlay, onBackgroundTrackMediaFailedPlaying, onBackgroundTrackMediaStatusChanged, onMediaReadyToPlay, {
            looping: true
        });
    } else {
        if (this.useCordovaMediaObjectForBackgroundMusic()) {
            backgroundTrackMedia = this._createCordovaMedia(this._backgroundTrackUrl, onBackgroundTrackMediaSuccessPlay, onBackgroundTrackMediaFailedPlaying, onBackgroundTrackMediaStatusChanged, onMediaReadyToPlay);
        } else {

            backgroundTrackMedia = this._createSoundManagerMedia(this._backgroundTrackUrl, onBackgroundTrackMediaSuccessPlay, onBackgroundTrackMediaFailedPlaying, onBackgroundTrackMediaStatusChanged, onMediaReadyToPlay, {
                looping: true
            });
        }
    }
    this._backgroundTrackMedia = backgroundTrackMedia;
    window.log("Background audio media was created for " + backgroundTrackUrl);

}).bind(this));
this._backgroundTrackLoadingPromise = p;
p.then((function() {
    this._backgroundTrackMedia._completelyLoaded = true;
    this._backgroundTrackLoadingPromise = null;
    window.log("Background audio media for " + backgroundTrackUrl + " is loaded and ready");
}).bind(this));
return p;
}

},

/*
_getLocalMediaAssetUrl: function(webUrl) {
const TREBBLE_URL_PREFIX = "web.trebble.fm/";
return webUrl.substring(webUrl.indexOf(TREBBLE_URL_PREFIX) + TREBBLE_URL_PREFIX.length);
},
*/

deleteAllBackgroundMusicDownloadedLocally: function(){
FileUploadHelper.getInstance().deleteAllTemporaryDownloadedFiles();
},


_getLocalMediaAssetUrl : function(remoteURL){
return FileUploadHelper.getInstance().getLocalFileURLDownloadedTemporary(remoteURL);
},

_createCordovaMedia: function(audioUrl, onSuccess, onError, onStatus, onMediaReadyToPlay) {
    let backgroundTrackMedia;
if (USE_NATIVE_AUDIO_FOR_BACKGROUND && window.isIOSMobileDevice) {
const soundNativeAudioMediaParams = {};
soundNativeAudioMediaParams.uri = this._getLocalMediaAssetUrl(audioUrl);
soundNativeAudioMediaParams.onSuccess = onSuccess;
soundNativeAudioMediaParams.onError = onError;
soundNativeAudioMediaParams.onStatus = onStatus;
soundNativeAudioMediaParams.onMediaReadyToPlay = onMediaReadyToPlay;
backgroundTrackMedia = new SoundNativeAudio(soundNativeAudioMediaParams);
backgroundTrackMedia.setLooping(true);
return backgroundTrackMedia
} else {
     backgroundTrackMedia = new window.Media(audioUrl, onSuccess, onError, onStatus);
backgroundTrackMedia.successCallback.media = backgroundTrackMedia;
backgroundTrackMedia.errorCallback.media = backgroundTrackMedia;
backgroundTrackMedia.statusCallback.media = backgroundTrackMedia;
onMediaReadyToPlay();
return backgroundTrackMedia;
}
},

_createSoundManagerMedia: function(audioUrl, onSuccess, onError, onStatus, onMediaReadyToPlay, options) {
const soundManagerMediaOptions = options || {};
const soundManagerMediaParams = {};
soundManagerMediaParams.uri = audioUrl;
soundManagerMediaParams.onSuccess = onSuccess;
soundManagerMediaParams.onError = onError;
soundManagerMediaParams.onStatus = onStatus;
soundManagerMediaParams.onMediaReadyToPlay = onMediaReadyToPlay;
soundManagerMediaParams.autoLoad = true;
const soundManagerMedia =  new SoundManagerMedia(soundManagerMediaParams);
soundManagerMedia.setLooping(!!soundManagerMediaOptions.looping);
this._applyPreferredPlaybackOutputDeviceToSoundManagerMedia(soundManagerMedia);
return soundManagerMedia;
},

_getEffectivePreferredPlaybackOutputDeviceId: function() {
    const localStorageHelper = LocalStorageHelper.getInstance();
    if(localStorageHelper.getPreferredPlaybackOutputDeviceSelectionWasExplicit && !localStorageHelper.getPreferredPlaybackOutputDeviceSelectionWasExplicit()){
        return "";
    }

    const preferredPlaybackOutputDeviceId = localStorageHelper.getPreferredPlaybackOutputDeviceId();
    if(!preferredPlaybackOutputDeviceId || preferredPlaybackOutputDeviceId === "default"){
        return "";
    }

    return preferredPlaybackOutputDeviceId;
},

_applyPreferredPlaybackOutputDeviceToNativeMediaElement: function(mediaElement) {
    if(!mediaElement || typeof mediaElement.setSinkId !== "function" || !this.isSpeakerOutputSelectionSupported()){
        return RSVP.Promise.resolve(null);
    }

    const preferredOutputDeviceId = this._getEffectivePreferredPlaybackOutputDeviceId();
    const targetSinkId = preferredOutputDeviceId || "";
    const applySinkId = (function(sinkIdToApply) {
        if(mediaElement.sinkId === sinkIdToApply){
            return RSVP.Promise.resolve(sinkIdToApply);
        }

        return RSVP.Promise.resolve(mediaElement.setSinkId(sinkIdToApply));
    }).bind(this);

    const fallBackToDefaultOutput = (function() {
        return applySinkId("").then((function() {
            return "";
        }).bind(this)).catch(function(defaultOutputError) {
            console.warn("Unable to fall back to the default speaker output for native recorded media playback", defaultOutputError);
            return null;
        });
    }).bind(this);

    if(!targetSinkId){
        return applySinkId("").catch(function(error) {
            console.warn("Unable to route native recorded media playback to the default speaker output", error);
            return null;
        });
    }

    return this.getAudioOutputDevicesAvailableWithoutPrompt().catch(function() {
        return [];
    }).then((function(audioOutputDevices) {
        if(audioOutputDevices && audioOutputDevices.length > 0){
            const hasMatchingOutputDevice = audioOutputDevices.some(function(deviceInfo) {
                return deviceInfo && deviceInfo.deviceId === targetSinkId;
            });

            if(!hasMatchingOutputDevice){
                console.warn("The preferred speaker output device is no longer available for native recorded media playback", {
                    preferredOutputDeviceId: preferredOutputDeviceId
                });
                return fallBackToDefaultOutput();
            }
        }

        return applySinkId(targetSinkId).catch((function(error) {
            console.warn("Unable to apply the preferred speaker output device to native recorded media playback", error);
            return fallBackToDefaultOutput();
        }).bind(this));
    }).bind(this));
},

_createNativeBrowserRecordedMediaPlayback: function(audioUrl, onSuccess, onError, onStatus) {
    const audioRecorderHelper = this;
    const audioEl = new Audio(audioUrl);
    let released = false;
    let completed = false;
    let failed = false;
    let preferredOutputPromise = null;

    const notifyStatus = function(status) {
        if (onStatus && !released) {
            onStatus(status);
        }
    };
    const notifyError = function(error) {
        if (released) {
            return;
        }
        if (onError) {
            onError(error);
        }
    };
    const failPlayback = function(error) {
        if (released || failed) {
            return;
        }

        failed = true;
        completed = true;
        notifyStatus(4);
        notifyError(error);
    };

    audioEl.preload = "auto";
    audioEl.volume = 1;

    const onPlaying = function() {
        notifyStatus(2);
    };
    const onPause = function() {
        if (!completed) {
            notifyStatus(3);
        }
    };
    const onEnded = function() {
        if (released || completed) {
            return;
        }
        completed = true;
        notifyStatus(4);
        if (onSuccess) {
            onSuccess();
        }
    };
    const onAudioError = function() {
        failPlayback(audioEl.error || new Error("Unable to play the recorded audio."));
    };

    audioEl.addEventListener("playing", onPlaying);
    audioEl.addEventListener("pause", onPause);
    audioEl.addEventListener("ended", onEnded);
    audioEl.addEventListener("error", onAudioError);

    const ensurePreferredOutputDeviceApplied = function() {
        if(!preferredOutputPromise){
            preferredOutputPromise = RSVP.Promise.resolve(audioRecorderHelper._applyPreferredPlaybackOutputDeviceToNativeMediaElement(audioEl)).catch(function(error) {
                console.warn("Unable to prepare the preferred speaker output device for native recorded media playback", error);
                return null;
            });
        }

        return preferredOutputPromise;
    };

    return {
        play: function() {
            if (released) {
                return;
            }
            completed = false;
            failed = false;
            ensurePreferredOutputDeviceApplied().then(function() {
                const playPromise = audioEl.play ? audioEl.play() : null;
                if (playPromise && playPromise.catch) {
                    playPromise.catch(failPlayback);
                }
            }).catch(failPlayback);
        },
        pause: function() {
            if (!released && audioEl.pause) {
                audioEl.pause();
            }
        },
        stop: function() {
            if (released) {
                return;
            }
            completed = true;
            if (audioEl.pause) {
                audioEl.pause();
            }
            try {
                audioEl.currentTime = 0;
            } catch (_) {
                // Ignore seek reset failures on partially loaded media.
            }
            notifyStatus(4);
        },
        release: function() {
            if (released) {
                return;
            }
            released = true;
            if (audioEl.pause) {
                audioEl.pause();
            }
            audioEl.removeEventListener("playing", onPlaying);
            audioEl.removeEventListener("pause", onPause);
            audioEl.removeEventListener("ended", onEnded);
            audioEl.removeEventListener("error", onAudioError);
            audioEl.removeAttribute("src");
            if (audioEl.load) {
                audioEl.load();
            }
        },
        setVolume: function(volume) {
            if (released) {
                return;
            }
            const normalizedVolume = volume > 1 ? (volume / 100) : volume;
            audioEl.volume = Math.max(0, Math.min(1, normalizedVolume));
        },
        setOutputDeviceId: function(outputDeviceId) {
            if(released || !audioRecorderHelper.isSpeakerOutputSelectionSupported() || typeof audioEl.setSinkId !== "function"){
                return RSVP.Promise.resolve(null);
            }

            preferredOutputPromise = RSVP.Promise.resolve(audioEl.setSinkId(outputDeviceId && outputDeviceId !== "default" ? outputDeviceId : "")).catch(function(error) {
                console.warn("Unable to update the speaker output device for native recorded media playback", error);
                return null;
            });
            return preferredOutputPromise;
        }
    };
},

_applyPreferredPlaybackOutputDeviceToSoundManagerMedia: function(soundManagerMedia) {
if(!soundManagerMedia || !soundManagerMedia.setOutputDeviceId || !this.isSpeakerOutputSelectionSupported()){
    return RSVP.Promise.resolve(null);
}

return RSVP.Promise.resolve(soundManagerMedia.setOutputDeviceId(this._getEffectivePreferredPlaybackOutputDeviceId())).catch(function(error) {
    console.warn("Unable to apply the preferred speaker output device", error);
    return null;
});
},

applyPreferredPlaybackOutputDeviceToActivePlayers: function() {
    const outputDevicePromises = [];

    if(this._backgroundTrackMedia){
        outputDevicePromises.push(this._applyPreferredPlaybackOutputDeviceToSoundManagerMedia(this._backgroundTrackMedia));
    }
    if(this._currentMediaPlaying){
        if(this._currentMediaPlaying.setOutputDeviceId && this.isSpeakerOutputSelectionSupported()){
            outputDevicePromises.push(RSVP.Promise.resolve(this._currentMediaPlaying.setOutputDeviceId(this._getEffectivePreferredPlaybackOutputDeviceId())).catch(function(error) {
                console.warn("Unable to apply the preferred speaker output device to the active recorded media player", error);
                return null;
            }));
        }else{
            outputDevicePromises.push(this._applyPreferredPlaybackOutputDeviceToSoundManagerMedia(this._currentMediaPlaying));
        }
    }

    return RSVP.all(outputDevicePromises);
},

useCordovaMediaObjectForBackgroundMusic: function() {
return (USE_CORDOVA_MEDIA_FOR_BACKGROUND || USE_NATIVE_AUDIO_FOR_BACKGROUND) && window.waitForCordovaToLoad;
},

_playBackgroundTrackIfApplicable: function() {
window.log('Attempt to play background music');
if (this._backgroundTrackMedia && this._backgroundTrackMedia._completelyLoaded) {
//this._backgroundTrackMedia.stop();
window.log('Playing background music');
this._backgroundTrackMedia.play({ numberOfLoops: 9999 });// We are using this to make sure that the content loops until the audio recording is done playing (Supported by Cordova Media only)
}
},

isSameRequestIdMatching: function(requestIdToTestAgainst) {
return !requestIdToTestAgainst || this._playRecordingRequestId == requestIdToTestAgainst;
},

_stopBackgroundTrackIfApplicable: function(requestId) {
if (this._backgroundTrackMedia && this._backgroundTrackMedia._completelyLoaded) {
if (this.isSameRequestIdMatching(requestId)) {
    this._backgroundTrackMedia.stop();
}
}
},



_playQueue: function(backgroundTrackUrl, backgroundTrackLoadingHandler, playRecordingRequestId) {
if (this._recordingFilenameInPlayQueue.length > 0) {
if (backgroundTrackLoadingHandler) {
    backgroundTrackLoadingHandler(true);
}
window.log("About to start playing queue with url " + backgroundTrackUrl);
return this._downloadBackgroundTrackLocallyIfApplicable(backgroundTrackUrl).then((function(){
    return this._loadBackgroundTrackIfApplicable(backgroundTrackUrl, playRecordingRequestId)
}).bind(this)).then((function() {
    if (backgroundTrackLoadingHandler) {
        backgroundTrackLoadingHandler(false);
    }
    window.log("_loadBackgroundTrackIfApplicable for " + backgroundTrackUrl + " completed ");
    return new RSVP.Promise((function(resolve, reject) {

        const filenameToPlay = this._recordingFilenameInPlayQueue[0];
        window.log("About to load media file " + filenameToPlay);
        let mediaToPlay = null;
        const onMediaSuccessPlay = (function() {
            window.log("media with background " + backgroundTrackUrl + "finished playing");
            if (this.isSameRequestIdMatching(playRecordingRequestId)) {
                if (this._currentMediaPlaying && this._currentMediaPlaying.release) {
                    this._currentMediaPlaying.release();
                }
                this._recordingFilenameInPlayQueue.splice(0, 1);
                if (this._recordingFilenameInPlayQueue.length > 0) {
                    window.log("There is more to play so going to the next audio with background " + backgroundTrackUrl);
                    this._stopBackgroundTrackIfApplicable(playRecordingRequestId);
                    this._playQueue(backgroundTrackUrl, backgroundTrackLoadingHandler, playRecordingRequestId).then(resolve).catch(reject);
                } else {
                    if (this.isSameRequestIdMatching(playRecordingRequestId)) {
                        //This might be different if the user changed the background music
                        window.log("Done playing so fading out the background music " + backgroundTrackUrl);
                        this.fadeOutroAndEndBackground(resolve, reject, playRecordingRequestId);
                    } else {
                        window.log("Done playing  background music " + backgroundTrackUrl + "and returning");
                        resolve();
                    }
                }
            }
        }).bind(this);
        const onMediaFailedPlaying = (function(err) {
            window.log("There was an error for media " + filenameToPlay + " with  background music " + backgroundTrackUrl);
            console.error(err);
            this._currentMediaPlayingStatus = null;
            this._stopBackgroundTrackIfApplicable(playRecordingRequestId);
            if (true) {
//Ignore any playback error
window.log("We will ignore playback error for media " + filenameToPlay + " with  background music " + backgroundTrackUrl);
resolve();
} else {
window.log("Throwing the playback error for media " + filenameToPlay + " with  background music " + backgroundTrackUrl);
reject(err);
}
}).bind(this);
        const onMediaStatusChanged = this._onMediaStatusChanged.bind(this);
        if (window.device && window.device.platform == "browser" && !(window.trebble.useNativeRecording && "Media" in window)) {
            mediaToPlay = (typeof filenameToPlay === "string" && filenameToPlay.indexOf("blob:") === 0)
                ? this._createNativeBrowserRecordedMediaPlayback(filenameToPlay, onMediaSuccessPlay, onMediaFailedPlaying, onMediaStatusChanged)
                : this._createSoundManagerMedia(filenameToPlay, onMediaSuccessPlay, onMediaFailedPlaying, onMediaStatusChanged, null);
        } else {
            mediaToPlay = new window.Media(filenameToPlay, onMediaSuccessPlay, onMediaFailedPlaying, onMediaStatusChanged);
        }
        this._currentMediaPlaying = mediaToPlay;
        this._playRecordingRequestId = playRecordingRequestId;
        this._currentMediaPlayingStatus = 0;
        window.log(" media " + filenameToPlay + " created with  background music " + backgroundTrackUrl);

        this._playBackgroundTrackIfApplicable();
        this._isPlayingRecording = true;
        if (backgroundTrackUrl) {
            if (!INTRO_IN_SEC) {
                INTRO_IN_SEC = 0
            }
            const backgroundTrackMedia = this._backgroundTrackMedia;
            setTimeout((function() {
                if (this.isSameRequestIdMatching(playRecordingRequestId)) {
                    window.log("About to start fading music " + backgroundTrackUrl);
                    this.fadeIntroBackgroundMusic(backgroundTrackMedia, playRecordingRequestId).then((function() {
                        window.log("Fade in for " + backgroundTrackUrl + " completed so about to start media playback");
                        if (this.isSameRequestIdMatching(playRecordingRequestId)) {
                            window.log("Fade in for " + backgroundTrackUrl + " completed  and  starting media playback");
                            if (!this._currentlyLoadedMediaNeedToBeStopped) {
                                mediaToPlay.play();
                            } else {
                                window.log("Current media needed to be stopped so we didn't play it!");
                                this._currentlyLoadedMediaNeedToBeStopped = false;
                                this._stopAndDestroyRecordingMedia(true);
                            }
                        } else {
                            window.log("Stopping background music and cancelling mediaPlay which was planned...");
            //this.stopPlayRecordingsIfApplicable(playRecordingRequestId);
            this._currentlyLoadedMediaNeedToBeStopped = false;
            this._stopAndDestroyRecordingMedia(true);
        }
    }).bind(this));
                } else {
                    window.log("marked current media playing stopped since a new request has been made");
                    this._currentlyLoadedMediaNeedToBeStopped = false;
                    this._stopAndDestroyRecordingMedia(true);
//if urls are different, this means that the user might have change the background music
//In that case, do nothing
}
}).bind(this), INTRO_IN_SEC * 1000);
        } else {
            mediaToPlay.play();
            window.log("Playing media with no background ");
        }



    }).bind(this));
}).bind(this)).catch((function(error) {
console.error(error);
window.alertErrorMessage(window.getI18n(ti18n, "SOMETHING_WENT_WRONG_WHILE_PLAYING_YOUR_CAPSULE"));
}).bind(this));
} else {
this._isPlayingRecording = false;
this._currentMediaPlaying = null;
this._currentMediaPlayingStatus = null;
return RSVP.Promise.resolve();
}
},

fadeIntroBackgroundMusic: function(backgroundTrackMediaToFadeIn, requestId) {
if (backgroundTrackMediaToFadeIn) {
if (!INTRO_FADE_IN_SEC) {
    INTRO_FADE_IN_SEC = 0
}
window.log("fadeIntroBackgroundMusic executing...")
if (INTRO_FADE_IN_SEC > 0) {
    let startVolume = this.useCordovaMediaObjectForBackgroundMusic() ? 1.0 : 100;
    let endVolume = this.useCordovaMediaObjectForBackgroundMusic() ? BACKGROUND_VOLUME_LEVEL / 100 : BACKGROUND_VOLUME_LEVEL;
    let increment = this.useCordovaMediaObjectForBackgroundMusic() ? FADE_INCREMENT / 100 : FADE_INCREMENT;
    return Utils.getInstance().fadeMediaTo(backgroundTrackMediaToFadeIn, INTRO_FADE_IN_SEC * 1000, startVolume, endVolume, increment)
} else {
    let endVolume = this.useCordovaMediaObjectForBackgroundMusic() ? BACKGROUND_VOLUME_LEVEL / 100 : BACKGROUND_VOLUME_LEVEL;
    backgroundTrackMediaToFadeIn.setVolume(endVolume);
    return RSVP.Promise.resolve();
}
} else {
window.log("fadeIntroBackgroundMusic skipped")
return RSVP.Promise.resolve();
}
},

fadeOutroBackgroundMusic: function() {
if (this._backgroundTrackMedia) {
if (!OUTRO_FADE_IN_SEC) {
    OUTRO_FADE_IN_SEC = 0
}
window.log("fadeOutroBackgroundMusic executing...")
if (OUTRO_FADE_IN_SEC > 0) {
    let endVolume = this.useCordovaMediaObjectForBackgroundMusic() ? 1.0 : 100;
    let startVolume = this.useCordovaMediaObjectForBackgroundMusic() ? BACKGROUND_VOLUME_LEVEL / 100 : BACKGROUND_VOLUME_LEVEL;
    let increment = this.useCordovaMediaObjectForBackgroundMusic() ? FADE_INCREMENT / 100 : FADE_INCREMENT;
    return Utils.getInstance().fadeMediaTo(this._backgroundTrackMedia, OUTRO_FADE_IN_SEC * 1000, startVolume, endVolume, increment)
} else {
    let endVolume = this.useCordovaMediaObjectForBackgroundMusic() ? 1.0 : 100;
    this._backgroundTrackMedia.setVolume(endVolume);
    return RSVP.Promise.resolve();
}
} else {
window.log("fadeOutroBackgroundMusic skipped")
return RSVP.Promise.resolve();
}
},

endBackgroundMusicWithFadeOut: function(requestId) {
if (this._backgroundTrackMedia && this.isSameRequestIdMatching(requestId)) {
if (!FADE_OUT_END_IN_SEC) {
    FADE_OUT_END_IN_SEC = 0
}
window.log("endBackgroundMusicWithFadeOut executing...")
if (OUTRO_FADE_IN_SEC > 0) {
    const startVolume = this.useCordovaMediaObjectForBackgroundMusic() ? 1.0 : 100;
    const endVolume = 0;
    const increment = this.useCordovaMediaObjectForBackgroundMusic() ? FADE_INCREMENT / 100 : FADE_INCREMENT;
    return Utils.getInstance().fadeMediaTo(this._backgroundTrackMedia, FADE_OUT_END_IN_SEC * 1000, startVolume, endVolume, increment)
} else {
    this._backgroundTrackMedia.setVolume(0);
    return RSVP.Promise.resolve();
}
} else {
window.log("endBackgroundMusicWithFadeOut skipped")
return RSVP.Promise.resolve();
}
},

fadeOutroAndEndBackground: function(resolve, reject, requestId) {
window.log("fadeOutroAndEndBackground executing for " + requestId + " ...")
return this.fadeOutroBackgroundMusic().then((function() {
return new RSVP.Promise((function(resolve, reject) {
    setTimeout(resolve, BACKGROUND_BUFFER_AT_THE_END_IN_SEC * 1000);
}).bind(this))
}).bind(this)).then((function() {
if (this.isSameRequestIdMatching(requestId)) {
    return this.endBackgroundMusicWithFadeOut(requestId);
} else {
    window.log("ignore endBackgroundMusicWithFadeOut commmand for " + requestId + " ...")
}
}).bind(this)).then((function() {
if (this.isSameRequestIdMatching(requestId)) {
    return this._stopBackgroundTrackIfApplicable(requestId);
} else {
    window.log("ignore _stopBackgroundTrackIfApplicable commmand for " + requestId + " ...")
}
}).bind(this)).then(resolve).catch(reject);
},

_stopAndDestroyRecordingMedia: function(releaseOnly) {
try {
if (this._currentMediaPlaying) {
    if (!releaseOnly) {
        this._currentMediaPlaying.stop();
    }
    this._currentMediaPlaying.release();
}

this._isPlayingRecording = false;
this._currentMediaPlaying = null;
for (let i in this._onMediaSuccessfullyStoppedCBs) {
    let cb = this._onMediaSuccessfullyStoppedCBs[i];
    cb();
}
this._onMediaSuccessfullyStoppedCBs = [];
this._onMediaFailedBeingStoppedCBs = [];
this._currentMediaPlayingStatus = null;
window.log("Media was stopped and destroy .");
} catch (error) {
for (let i in this._onMediaFailedBeingStoppedCBs) {
    let cb = this._onMediaFailedBeingStoppedCBs[i];
    cb(error);
}
console.error("Something went wrong when stopping media .Error: " + error);
}
},

_onMediaStatusChanged: function(mediaStatus) {
window.log("Media status change " + mediaStatus);
this._currentMediaPlayingStatus = mediaStatus;
if (this._currentlyLoadedMediaNeedToBeStopped) {
window.log("Media need to stopped so stopping ...  Media status: " + mediaStatus);
this._currentlyLoadedMediaNeedToBeStopped = false;
const isPlaying = mediaStatus == 2;
this._stopAndDestroyRecordingMedia(!isPlaying)

}
},

_onSongReadyToBeStopped: function() {
if (this._currentMediaPlayingStatus != 0) {
if (this._currentlyLoadedMediaNeedToBeStopped) {
    this._currentMediaPlaying.stop();
    this._isPlayingRecording = false;
    this._currentMediaPlaying = null;
    this._currentlyLoadedMediaNeedToBeStopped = false;
    for (const i in this._onMediaSuccessfullyStoppedCBs) {
        const cb = this._onMediaSuccessfullyStoppedCBs[i];
        cb();
    }
    this._onMediaSuccessfullyStoppedCBs = [];
    this._onMediaFailedBeingStoppedCBs = [];
    this._onMediaSuccessfullyStoppedCBs = [];
    this._onMediaFailedBeingStoppedCBs = [];
    this._currentMediaPlayingStatus = null;
    //notify all listeners
}
}
},


isPlayingRecording: function() {
return this._isPlayingRecording;
},

stopPlayRecordingsIfApplicable: function() {
window.log(" stopPlayRecordingsIfApplicable  executing ...");
//if (this._isPlayingRecording) {
this._stopAndDestroyBackgroundTrack();
if (this._currentMediaPlayingStatus == 0) {
    window.log(" stopPlayRecordingsIfApplicable  waiting for current media status to change to stop");
    return new RSVP.Promise((function(resolve, reject) {
        this._onMediaSuccessfullyStoppedCBs.push(resolve);
        this._onMediaFailedBeingStoppedCBs.push(reject);
        this._currentlyLoadedMediaNeedToBeStopped = true;
    }).bind(this)).then((function() {
        /*if(this._currentMediaPlaying ){
          this._currentMediaPlaying.pause();
          this._currentMediaPlaying.release? this._currentMediaPlaying.release(): null;
      }
      this._isPlayingRecording = false;
      this._currentMediaPlaying = null ;
      this._currentMediaPlayingStatus = null ;*/
      this._stopAndDestroyRecordingMedia();
  }).bind(this));
} else {
    /*if(this._currentMediaPlaying){
      this._currentMediaPlaying.stop();
      this._currentMediaPlaying.release? this._currentMediaPlaying.release(): null;
  }
  this._isPlayingRecording = false;
  this._currentMediaPlaying = null ;
  this._currentMediaPlayingStatus = null ;*/
  this._stopAndDestroyRecordingMedia();
  window.log(" stopPlayRecordingsIfApplicable  stopped right away");
  return RSVP.Promise.resolve();
}
/*} else {
  console.error(" stopPlayRecordingsIfApplicable  nothing playing so go ahead");
  this._stopBackgroundTrackIfApplicable(); 
  return RSVP.Promise.resolve();
}*/
},

});

const AudioRecorderHelperInstance = new AudioRecorderHelper();

export default {
    getInstance: function() {
        return AudioRecorderHelperInstance;
    }
};
